// src/LibraryViewer.js
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Papa from "papaparse";


import {
  collection, getDocs,  query, doc, setDoc, serverTimestamp, orderBy, deleteDoc, addDoc,
  onSnapshot,
  updateDoc,
  arrayRemove,
  getDoc // <-- ADD THIS
} from "firebase/firestore";



import { ref, getDownloadURL, uploadBytes, deleteObject } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";
import { db, storage, auth } from "./firebase";
import Menu from "./Menu";
import { useHostVenues } from "./venues";


// --- HELPER FUNCTIONS ---

// Utility function to extract Spotify embed URL from iframe code
function extractSpotifyUrl(iframeCode) {
  if (!iframeCode || typeof iframeCode !== 'string') return null;
  
  // Try to extract src attribute from iframe string
  const srcMatch = iframeCode.match(/src=["']([^"']+)["']/i);
  if (srcMatch && srcMatch[1]) {
    const url = srcMatch[1].trim();
    // Check if it's a Spotify embed URL
    if (url.includes('open.spotify.com/embed')) {
      return url;
    }
  }
  
  // If no match, check if the string itself is already a Spotify URL
  if (iframeCode.includes('open.spotify.com/embed')) {
    return iframeCode.trim();
  }
  
  return null;
}

// Extract track ID from Spotify embed URL
function extractSpotifyTrackId(spotifyUrl) {
  if (!spotifyUrl) return null;
  const match = spotifyUrl.match(/\/track\/([a-zA-Z0-9]+)/);
  return match ? match[1] : null;
}

// Construct Spotify preview URL directly (no API credentials needed!)
function getSpotifyPreviewUrl(trackId) {
  if (!trackId) return null;
  // Direct preview URL pattern - publicly accessible, no auth needed
  return `https://p.scdn.co/mp3-preview/${trackId}`;
}

const CSV_TRACK_STORAGE_KEY = "libraryViewerCsvTracks";
const DEFAULT_SORT_FIELD = "interestRating";
const DEFAULT_SORT_DIRECTION = "desc";
const ANSWER_MAX_LENGTH = 42;
const BREADCRUMBS_CLUE_MAX_LENGTH = 100;
const ANSWER_KEYS = ["answer", "A2", "A3", "A4", "A5", "A6"];

function reorderListImmutable(list, startIndex, endIndex) {
  const next = list.slice();
  const [moved] = next.splice(startIndex, 1);
  next.splice(endIndex, 0, moved);
  return next;
}
const BREADCRUMBS_ANSWER_KEYS = ["answer", "A2", "A3", "A4", "A5", "A6", "A7", "A8", "A9", "A10", "A11"];
const BREADCRUMBS_CLUE_KEYS = ["A2", "A3", "A4", "A5", "A6", "A7", "A8", "A9", "A10", "A11"];
const DEFAULT_FILTERS = {
  flaggedFilter: "All",
  musicFilter: "All",
  genreFilter: "",
  categoryFilter: "",
  venueFilter: "",
  venueUsageFilter: "All",
  hashtagFilter: ""
};

function normalizeHeaderValue(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function normalizeUrlKey(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeHashtag(value) {
  return String(value || "")
    .trim()
    .replace(/^#/, "")
    .toLowerCase();
}

function extractTagTokens(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.flatMap((entry) => extractTagTokens(entry));
  }

  const text = String(value);
  const hashtagMatches = text.match(/#[a-zA-Z0-9_-]+/g) || [];
  const plainTokens = text
    .split(/[,\s]+/)
    .map(token => token.trim())
    .filter(Boolean);

  return [...hashtagMatches, ...plainTokens];
}

/** Most frequent key in a count map (for "predominant" genre / category). */
function predominantFromCounts(countMap) {
  if (!countMap || countMap.size === 0) return "—";
  let bestKey = null;
  let bestCount = 0;
  countMap.forEach((count, key) => {
    if (count > bestCount) {
      bestCount = count;
      bestKey = key;
    }
  });
  return bestKey != null && bestKey !== "" ? bestKey : "—";
}

const TAG_STATS_TABLE_PAGE_SIZE = 80;

/**
 * One pass: id → question text map plus per-tag aggregates (avg interest, counts, predominant genre/category).
 */
function buildTagStatsData(questions) {
  const questionById = new Map();
  if (!Array.isArray(questions) || questions.length === 0) {
    return { tagRows: [], questionById };
  }
  const byTag = new Map();

  questions.forEach((q) => {
    if (q?.id) {
      const t = (q.question && String(q.question).trim()) || "(no question text)";
      questionById.set(q.id, t);
    }

    const rawTags = extractTagTokens(q?.tags)
      .map((t) => normalizeHashtag(t))
      .filter(Boolean);
    const uniqueTags = [...new Set(rawTags)];
    if (uniqueTags.length === 0) return;

    const r = Number(q?.interestRating);
    const rating = Number.isFinite(r) ? r : null;
    const genre = (q?.genre && String(q.genre).trim()) || "—";
    const cat = (q?.questionType && String(q.questionType).trim()) || "—";
    const id = q?.id || "";

    uniqueTags.forEach((tag) => {
      if (!byTag.has(tag)) {
        byTag.set(tag, {
          ratings: [],
          genreCount: new Map(),
          catCount: new Map(),
          items: [],
        });
      }
      const o = byTag.get(tag);
      if (rating !== null) o.ratings.push(rating);
      o.genreCount.set(genre, (o.genreCount.get(genre) || 0) + 1);
      o.catCount.set(cat, (o.catCount.get(cat) || 0) + 1);
      o.items.push({ id });
    });
  });

  const rows = [];
  byTag.forEach((o, tag) => {
    const n = o.ratings.length;
    const avgPop = n > 0 ? o.ratings.reduce((a, b) => a + b, 0) / n : 0;
    o.items.sort((a, b) => String(a.id).localeCompare(String(b.id)));
    rows.push({
      tag,
      count: o.items.length,
      avgPopularity: avgPop,
      predominantGenre: predominantFromCounts(o.genreCount),
      predominantCategory: predominantFromCounts(o.catCount),
      items: o.items,
    });
  });

  rows.sort((a, b) => a.tag.localeCompare(b.tag));
  return { tagRows: rows, questionById };
}

function compareTagStatsRows(a, b, key, dir) {
  const sign = dir === "asc" ? 1 : -1;
  let cmp = 0;
  switch (key) {
    case "tag":
      cmp = a.tag.localeCompare(b.tag);
      break;
    case "count":
      cmp = a.count - b.count;
      break;
    case "avgPopularity":
      cmp = a.avgPopularity - b.avgPopularity;
      break;
    case "predominantGenre":
      cmp = String(a.predominantGenre).localeCompare(String(b.predominantGenre), undefined, { sensitivity: "base" });
      break;
    case "predominantCategory":
      cmp = String(a.predominantCategory).localeCompare(String(b.predominantCategory), undefined, { sensitivity: "base" });
      break;
    default:
      cmp = 0;
  }
  if (cmp !== 0) return sign * cmp;
  return a.tag.localeCompare(b.tag);
}

function sanitizeFilename(value) {
  return String(value || "")
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function getExtensionFromUrl(url) {
  try {
    const pathname = new URL(url).pathname;
    const lastSegment = pathname.split("/").pop() || "";
    const match = lastSegment.match(/\.([a-zA-Z0-9]+)$/);
    return match ? match[1].toLowerCase() : null;
  } catch (error) {
    return null;
  }
}

function getExtensionFromContentType(contentType) {
  if (!contentType) return null;
  const type = contentType.split(";")[0].trim().toLowerCase();
  if (type === "audio/mpeg" || type === "audio/mp3") return "mp3";
  if (type === "audio/mp4") return "m4a";
  if (type === "audio/wav") return "wav";
  if (type === "audio/ogg") return "ogg";
  return null;
}

function extractCsvTrack(row) {
  const normalized = {};
  Object.keys(row || {}).forEach((key) => {
    normalized[normalizeHeaderValue(key)] = row[key];
  });

  const trackName = String(
    normalized.trackname || normalized.track || ""
  ).trim();
  const artistNames = String(
    normalized.artistnames || normalized.artistname || normalized.artist || ""
  ).trim();
  const previewUrl = String(
    normalized.trackpreviewurl ||
      normalized.previewurl ||
      normalized.trackpreview ||
      ""
  ).trim();

  return {
    trackName,
    artistNames,
    previewUrl,
  };
}

const STOP_WORDS = new Set([
  "the", "and", "for", "are", "but", "not", "you", "all", "can", "had", "her", "his", "was", "one", "our", "out",
  "get", "has", "him", "how", "its", "may", "now", "old", "see", "way", "who", "did", "got", "let", "put", "say",
  "she", "too", "use", "any", "day", "new", "off", "own", "than", "these", "into", "with", "from", "that", "this"
]);

function tokenizeText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 2);
}

function collectQuestionText(editData) {
  if (!editData) return "";
  const answerKeys = ["answer", "A2", "A3", "A4", "A5", "A6"];
  const answers = answerKeys
    .map((key) => (editData[key] || "").toString())
    .filter((val) => val.trim().length > 0);
  return [editData.question || "", ...answers].join(" ");
}

function scoreTrackMatch(track, questionTokensSet, questionText) {
  if (!track?.previewUrl) return 0;
  const trackName = track.trackName || "";
  const artistNames = track.artistNames || "";
  const normalizedQuestion = questionText.toLowerCase();
  const normalizedTrackName = trackName.toLowerCase().trim();
  const normalizedArtist = artistNames.toLowerCase().trim();
  let score = 0;

  if (normalizedTrackName && normalizedQuestion.includes(normalizedTrackName)) {
    score += 5;
  }
  if (normalizedArtist && normalizedQuestion.includes(normalizedArtist)) {
    score += 3;
  }

  const trackTokens = tokenizeText(`${trackName} ${artistNames}`);
  trackTokens.forEach((token) => {
    if (!STOP_WORDS.has(token) && questionTokensSet.has(token)) {
      score += 1;
    }
  });

  return score;
}

function calculateSimilarity(s1, s2) {
  let longer = s1;
  let shorter = s2;
  if (s1.length < s2.length) {
    longer = s2;
    shorter = s1;
  }
  let longerLength = longer.length;
  if (longerLength === 0) return 1.0;
  return (longerLength - editDistance(longer, shorter)) / parseFloat(longerLength);
}

function editDistance(s1, s2) {
  s1 = s1.toLowerCase();
  s2 = s2.toLowerCase();
  const costs = [];
  for (let i = 0; i <= s1.length; i++) {
    let lastValue = i;
    for (let j = 0; j <= s2.length; j++) {
      if (i === 0) costs[j] = j;
      else if (j > 0) {
        let newValue = costs[j - 1];
        if (s1.charAt(i - 1) !== s2.charAt(j - 1))
          newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
        costs[j - 1] = lastValue;
        lastValue = newValue;
      }
    }
    if (i > 0) costs[s2.length] = lastValue;
  }
  return costs[s2.length];
}

// --- UI COMPONENTS ---

const BobPopup = ({ message, onClose }) => (
  <div className="bob-popup-overlay">
    <div className="bob-popup-content">
      <div className="bob-popup-header"><span role="img" aria-label="warning">⚠️</span> Warning</div>
      <p>{message}</p>
      <button onClick={onClose}>OK</button>
    </div>
  </div>
);

function BobModal({
  open,
  onClose,
  title = "Quizmaster Bob says",
  message,
  confirmText = "OK",
  imageSrc = "/media/Quizmasterbobpopup.png"
}) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2147483647
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 92vw)",
          background:
            "linear-gradient(180deg, rgba(34,42,58,0.78) 0%, rgba(27,33,48,0.78) 100%)",
          border: "1px solid rgba(255,255,255,0.20)",
          borderRadius: 16,
          boxShadow: "0 18px 60px rgba(0,0,0,0.45)",
          padding: "18px 18px 16px",
          color: "#eaf2ff",
          textAlign: "center"
        }}
      >
        <div
          style={{
            fontFamily: "'Montserrat','Open Sans',Arial,sans-serif",
            fontWeight: 900,
            fontSize: "1.2rem",
            color: "#ffe066",
            textShadow: "2px 2px 10px #222, 0 0 14px #09f",
            marginBottom: 6
          }}
        >
          {title}
        </div>

        <img
          src={imageSrc}
          alt="Quizmaster Bob"
          style={{
            display: "block",
            width: 96,
            height: 96,
            objectFit: "cover",
            borderRadius: "50%",
            margin: "2px auto 10px",
            boxShadow: "0 6px 22px rgba(0,0,0,0.35)",
            border: "2px solid rgba(255,255,255,0.75)"
          }}
        />

        <div style={{ fontSize: "1.05rem", margin: "6px 4px 14px", whiteSpace: "pre-line" }}>
          {message}
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          <button
            onClick={onClose}
            style={{
              background: "#ffc107",
              border: "none",
              borderRadius: 10,
              padding: "8px 16px",
              fontWeight: 800,
              cursor: "pointer",
              color: "#1b1f2a"
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

const ConfirmationPopup = ({ message, onConfirm, onCancel }) => (
  <div className="bob-popup-overlay">
    <div className="bob-popup-content">
      <div className="bob-popup-header"><span role="img" aria-label="warning">⚠️</span> Confirm Delete</div>
      <p>{message}</p>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '20px' }}>
        <button 
          onClick={onCancel} 
          style={{
            background: '#6c757d', color: 'white', border: 'none',
            borderRadius: '8px', padding: '10px 25px', fontSize: '1rem',
            fontWeight: 'bold', cursor: 'pointer'
          }}
        >
          Cancel
        </button>
        <button 
          onClick={onConfirm} 
          style={{
            background: '#dc3545', color: 'white', border: 'none',
            borderRadius: '8px', padding: '10px 25px', fontSize: '1rem',
            fontWeight: 'bold', cursor: 'pointer'
          }}
        >
          Delete
        </button>
      </div>
    </div>
  </div>
);

const AudioPopup = ({ url, onClose, onDelete }) => {
  if (!url) return null;
  return (
    <div onClick={onClose} className="media-popup-overlay">
      <div onClick={(e) => e.stopPropagation()} style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
        <div style={{ background: '#222', padding: '20px', borderRadius: '8px' }}>
          <audio src={url} controls autoPlay style={{ width: '400px' }}>
            Your browser does not support the audio element.
          </audio>
        </div>
        <button onClick={onClose} style={{ position: 'absolute', top: 10, right: 10, ...buttonStyles }}>Close</button>
        <button onClick={onDelete} style={{ position: 'absolute', top: 10, left: 10, ...buttonStyles, background: '#dc3545' }}>Delete</button>
      </div>
    </div>
  );
};

const MediaPopup = ({ url, onClose, onDelete }) => {
  if (!url) return null;
  const isVideo = url.match(/\.(mp4|webm|mov|m4v)/i);
  return (
    <div onClick={onClose} className="media-popup-overlay">
      <div onClick={(e) => e.stopPropagation()} style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
        {isVideo ? <video src={url} controls autoPlay /> : <img src={url} alt="Media full view" />}
        <button onClick={onClose} style={{ position: 'absolute', top: 10, right: 10, ...buttonStyles }}>Close</button>
        <button onClick={onDelete} style={{ position: 'absolute', top: 10, left: 10, ...buttonStyles, background: '#dc3545' }}>Delete</button>
      </div>
    </div>
  );
};

const buttonStyles = {
    background: '#007bff', color: 'white', border: 'none',
    borderRadius: '5px', padding: '8px 15px', cursor: 'pointer',
    fontWeight: 'bold', boxShadow: '0 2px 5px rgba(0,0,0,0.3)'
};

const AnswerButton = ({
  answerKey,
  value,
  isCorrect,
  questionType,
  onInputChange,
  onToggle,
  answerIndex,
  stats,
  statsLoading,
  showStats,
  displayLabel,
  useClueIcon
}) => {
  const isMainAnswer = answerKey === 'answer';
  const isMultiAnswer = questionType === 'MULTIANSWER';
  const isMultiAnswerCorrect = isMultiAnswer && typeof value === 'string' && value.startsWith('[') && value.endsWith(']');
  
  const showCheckmark = isMultiAnswer ? isMultiAnswerCorrect : isMainAnswer;
  
  const getButtonClass = () => {
    if (questionType === 'TRUE/FALSE') {
      return value === 'TRUE' ? 'correct' : 'incorrect';
    }
    if (questionType === 'OUTOFORDER') {
      return isMainAnswer ? "correct" : "incorrect";
    }
    if (isMultiAnswer) {
      return isMultiAnswerCorrect ? 'correct' : 'incorrect';
    }
    // BREADCRUMBS clues (useClueIcon): use neutral/clue style instead of incorrect
    if (useClueIcon) return 'clue';
    return isMainAnswer ? 'correct' : 'incorrect';
  };

  const handleIconClick = () => {
    if (questionType === 'TRUE/FALSE') {
      onToggle();
      return;
    }
    if (questionType === "OUTOFORDER") {
      return;
    }
    if (questionType === 'MULTIANSWER') {
      const trimmedValue = value ? value.trim() : '';
      const newValue = isMultiAnswerCorrect ? trimmedValue.slice(1, -1) : `[${trimmedValue}]`;
      onInputChange(answerKey, newValue);
      return;
    }
    if (isMainAnswer) return;
  };

  const handleNearestAnswerChange = (e) => {
    if (/^-?\d*\.?\d*$/.test(e.target.value)) onInputChange(answerKey, e.target.value);
  };

  const isNearestAnswer = (questionType === 'NEARESTANSWER' || questionType === 'JACKPOT') && isMainAnswer;
  const isReadOnly = questionType === 'TRUE/FALSE';

  const displayIcon = questionType === "OUTOFORDER"
    ? (isMainAnswer ? "✓" : "✗")
    : useClueIcon
      ? "?"
      : (showCheckmark ? "✓" : "✗");

  return (
    <div className={`answer-button-editable ${getButtonClass()}`}>
      <span 
        className="answer-icon" 
        onClick={handleIconClick} 
        style={{
          cursor: questionType === "OUTOFORDER" ? "default" : (isReadOnly || questionType === "MULTIANSWER" ? "pointer" : "default"),
          userSelect: 'none' // Prevent text selection on the number
        }}
      >
        {displayIcon}
      </span>
      <input
        type="text"
        inputMode={isNearestAnswer ? 'decimal' : 'text'}
        value={value || ""}
        readOnly={isReadOnly}
        onChange={isNearestAnswer ? handleNearestAnswerChange : (e) => onInputChange(answerKey, e.target.value)}
        className="answer-button-input"
        placeholder={displayLabel != null ? displayLabel : answerKey}
      />
      {showStats && (
        <div
          className={`answer-stat-badge ${statsLoading ? "loading" : ""}`}
          title={stats?.tooltip || "Chosen 0 times out of 0 responses"}
        >
          {statsLoading ? (
            <div className="answer-stat-loading">...</div>
          ) : (
            <>
              {stats?.percentText ? (
                <div className="answer-stat-percent">{stats.percentText}</div>
              ) : null}
              <div className="answer-stat-count">{stats?.countText || "0/0"}</div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// --- REPLACE SettingsModal COMPONENT ---
// --- REPLACE SettingsModal COMPONENT ---
// --- REPLACE SettingsModal COMPONENT ---
// --- REPLACE SettingsModal COMPONENT ---
const SettingsModal = ({
  open,
  onClose,
  filters,
  setFilters,
  onSortChange,
  currentSortField,
  currentSortDirection,
  onReset,
  onOpenTagStats,
  dbStats,
  genreOptions,
  questionTypeOptions,
  venueOptions
}) => {
  if (!open) return null;

  // Handlers
  const handleSelectChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  // Safe data access
  const categoryInfoSafe = dbStats?.categoryInfo || [];
  const genreInfoSafe = dbStats?.genreInfo || [];
  const totalQuestionsSafe = dbStats?.totalQuestions || 0;
  const flaggedCountSafe = dbStats?.flaggedCount || 0;

  return (
    // Overlay remains the same
    <div className="settings-modal-overlay">
      {/* --- Apply new class for wider style --- */}
      <div className="settings-modal-content wide">
        <div className="settings-modal-header">
          <h2>Settings</h2>
          <div className="settings-header-actions">
            <button className="settings-reset-button" type="button" onClick={onReset}>
              Reset to Defaults
            </button>
            <button className="settings-close-button" type="button" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        {/* --- Sort Section --- */}
        <fieldset className="settings-fieldset">
          <legend>Sort Order</legend>
          {/* --- New Row Wrapper --- */}
          <div className="settings-row">
            <div className="setting-item">
              <label htmlFor="sortField">Sort By:</label>
              <select id="sortField" value={currentSortField} onChange={(e) => onSortChange(e.target.value, currentSortDirection)}>
                <option value="interestRating">Interest Rating</option>
                <option value="question">Question Text</option>
                <option value="genre">Genre</option>
                <option value="questionType">Category</option>
                <option value="difficulty">Difficulty</option>
                <option value="dateAdded">Date Added</option>
                <option value="lastReviewedDate">Last Reviewed</option>
              </select>
            </div>
            <div className="setting-item">
              <label htmlFor="sortDirection">Sort Direction:</label>
              <select id="sortDirection" value={currentSortDirection} onChange={(e) => onSortChange(currentSortField, e.target.value)}>
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>
          </div>
        </fieldset>

        {/* --- Filter Section --- */}
        <fieldset className="settings-fieldset">
          <legend>Show only</legend>
          {/* --- New Row Wrapper --- */}
          <div className="settings-row four-cols"> {/* Add four-cols class */}
            {/* Flagged Filter - Changed to dropdown */}
            <div className="setting-item">
              <label htmlFor="flaggedFilter">Flagged:</label>
              <select
                id="flaggedFilter"
                value={filters.flaggedFilter}
                onChange={(e) => handleSelectChange('flaggedFilter', e.target.value)}
              >
                <option value="All">All</option>
                <option value="Flagged">Flagged</option>
                <option value="Unflagged">Unflagged</option>
              </select>
            </div>
            {/* Music Filter */}
            <div className="setting-item">
              <label htmlFor="musicFilter">Music:</label>
              <select
                id="musicFilter"
                value={filters.musicFilter}
                onChange={(e) => handleSelectChange('musicFilter', e.target.value)}
              >
                <option value="All">All</option>
                <option value="WithMusic">With Music</option>
                <option value="WithoutMusic">Without Music</option>
              </select>
            </div>
            {/* Genre Filter */}
            <div className="setting-item">
              <label htmlFor="genreFilter">Genre:</label>
              <select
                id="genreFilter"
                value={filters.genreFilter}
                onChange={(e) => handleSelectChange('genreFilter', e.target.value)}
              >
                <option value="">All Genres</option>
                {(genreOptions || []).map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            {/* Category Filter */}
            <div className="setting-item">
              <label htmlFor="categoryFilter">Category:</label>
              <select
                id="categoryFilter"
                value={filters.categoryFilter}
                onChange={(e) => handleSelectChange('categoryFilter', e.target.value)}
              >
                <option value="">All Categories</option>
                {(questionTypeOptions || []).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          {/* Venue Filter Row */}
          <div className="settings-row two-cols">
            <div className="setting-item">
              <label htmlFor="venueUsageFilter">Venue Usage:</label>
              <select
                id="venueUsageFilter"
                className="setting-input-spaced"
                value={filters.venueUsageFilter}
                onChange={(e) => handleSelectChange('venueUsageFilter', e.target.value)}
              >
                <option value="All">All</option>
                <option value="Used">Used</option>
                <option value="Unused">Unused</option>
              </select>
            </div>
            <div className="setting-item">
              <label htmlFor="venueFilter">Venue:</label>
              <select
                id="venueFilter"
                value={filters.venueFilter}
                onChange={(e) => handleSelectChange('venueFilter', e.target.value)}
              >
                <option value="">All Venues</option>
                {(venueOptions || []).map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>
          <div className="settings-hashtag-actions-row">
            <div className="setting-item full-width">
              <label htmlFor="hashtagFilter">Hashtag filter (comma/space separated):</label>
              <input
                id="hashtagFilter"
                type="text"
                className="setting-input-spaced"
                value={filters.hashtagFilter}
                onChange={(e) => handleSelectChange("hashtagFilter", e.target.value)}
                placeholder="#tag1, #tag2"
              />
            </div>
            {typeof onOpenTagStats === "function" && (
              <button className="settings-hashtag-stats-btn" type="button" onClick={onOpenTagStats}>
                Hashtag stats…
              </button>
            )}
          </div>
        </fieldset>

        {/* --- Stats Section --- */}
        <div className="stats-section">
          <h3>Database Stats</h3>
          <div className="stats-summary"> {/* Wrapper for totals */}
            <p>Total Questions: <strong>{totalQuestionsSafe}</strong></p>
            <p>Total Flagged: <strong>{flaggedCountSafe}</strong></p>
          </div>

          {/* --- New Columns Wrapper --- */}
          <div className="stats-columns">
            {/* --- Category Column --- */}
            <div className="stats-column">
              <h4>Category Breakdown:</h4>
              <ul className="stats-list">
                {categoryInfoSafe.map(({ type, count, percentage }) => (
                  <li key={`cat-${type}`}>
                    <span>{type || 'N/A'}:</span>
                    <span>{count} ({percentage}%)</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* --- Genre Column --- */}
            <div className="stats-column">
              <h4>Genre Breakdown:</h4>
              <ul className="stats-list">
                {genreInfoSafe.map(({ genre, count, percentage }) => (
                  <li key={`genre-${genre}`}>
                    <span>{genre || 'N/A'}:</span>
                    <span>{count} ({percentage}%)</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TAG_STATS_EXPAND_PAGE = 200;

/**
 * Windowed list for one expanded tag. Order follows `itemIds` (stable id sort from stats — no full-text
 * localeCompare here; that was O(n log n) on long strings and blocked the UI on large tags).
 */
function TagExpandedQuestionList({ itemIds, questionById }) {
  const pairs = useMemo(() => {
    return itemIds.map(({ id }) => ({
      id,
      text: id ? (questionById.get(id) ?? "(question not found)") : "(no id)",
    }));
  }, [itemIds, questionById]);

  const [shown, setShown] = useState(TAG_STATS_EXPAND_PAGE);
  const slice = pairs.slice(0, shown);
  const remaining = pairs.length - slice.length;

  return (
    <>
      <ul className="tag-stats-question-list" spellCheck={false}>
        {slice.map(({ id, text }, idx) => (
          <li key={id ? `${id}-${idx}` : `row-${idx}`}>{text}</li>
        ))}
      </ul>
      {remaining > 0 && (
        <button
          type="button"
          className="tag-stats-load-more"
          onClick={(e) => {
            e.stopPropagation();
            setShown((s) => Math.min(s + TAG_STATS_EXPAND_PAGE, pairs.length));
          }}
        >
          Load more ({remaining} remaining)
        </button>
      )}
    </>
  );
}

/**
 * One table row only (no second <tr> for questions). Inline expansion in a huge tbody forces full-table
 * reflow + O(n) sibling reconciliation; questions render in a single panel below the table instead.
 */
const TagStatsTableRow = React.memo(function TagStatsTableRow({
  tag,
  count,
  avgPopularity,
  predominantGenre,
  predominantCategory,
  isActive,
  onSelect,
}) {
  return (
    <tr
      className={`tag-stats-row-main${isActive ? " tag-stats-row-active" : ""}`}
      onClick={() => onSelect(tag)}
    >
      <td>
        {isActive ? "▼" : "▶"} #{tag}
      </td>
      <td>{count}</td>
      <td>{avgPopularity.toFixed(2)}</td>
      <td>{predominantGenre}</td>
      <td>{predominantCategory}</td>
    </tr>
  );
});

const TagStatsModal = React.memo(function TagStatsModal({ questions, onClose }) {
  const [activeDetailTag, setActiveDetailTag] = useState(null);
  const [sortKey, setSortKey] = useState("count");
  const [sortDir, setSortDir] = useState("desc");
  const [tagRows, setTagRows] = useState([]);
  const [questionById, setQuestionById] = useState(() => new Map());
  const [statsLoading, setStatsLoading] = useState(true);
  const [tagNameFilter, setTagNameFilter] = useState("");
  const [tagStatsPage, setTagStatsPage] = useState(0);

  useEffect(() => {
    setStatsLoading(true);
    setTagRows([]);
    setQuestionById(new Map());
    let cancelled = false;
    const run = () => {
      const { tagRows: nextRows, questionById: nextMap } = buildTagStatsData(questions);
      if (!cancelled) {
        setTagRows(nextRows);
        setQuestionById(nextMap);
        setStatsLoading(false);
      }
    };
    let idleId;
    if (typeof requestIdleCallback !== "undefined") {
      idleId = requestIdleCallback(run, { timeout: 2500 });
    } else {
      idleId = setTimeout(run, 1);
    }
    return () => {
      cancelled = true;
      if (typeof requestIdleCallback !== "undefined" && typeof cancelIdleCallback !== "undefined") {
        cancelIdleCallback(idleId);
      } else {
        clearTimeout(idleId);
      }
    };
  }, [questions]);

  const sortedRows = useMemo(() => {
    if (!tagRows.length) return tagRows;
    const rows = [...tagRows];
    rows.sort((a, b) => compareTagStatsRows(a, b, sortKey, sortDir));
    return rows;
  }, [tagRows, sortKey, sortDir]);

  const filteredSortedRows = useMemo(() => {
    const needle = tagNameFilter.trim().toLowerCase().replace(/^#/, "");
    if (!needle) return sortedRows;
    return sortedRows.filter((r) => r.tag.toLowerCase().includes(needle));
  }, [sortedRows, tagNameFilter]);

  const totalTagPages = Math.max(1, Math.ceil(filteredSortedRows.length / TAG_STATS_TABLE_PAGE_SIZE));

  const pageSlice = useMemo(() => {
    const start = tagStatsPage * TAG_STATS_TABLE_PAGE_SIZE;
    return filteredSortedRows.slice(start, start + TAG_STATS_TABLE_PAGE_SIZE);
  }, [filteredSortedRows, tagStatsPage]);

  useEffect(() => {
    setTagStatsPage((p) => Math.min(p, Math.max(0, totalTagPages - 1)));
  }, [totalTagPages]);

  useEffect(() => {
    if (!activeDetailTag) return;
    if (!filteredSortedRows.some((r) => r.tag === activeDetailTag)) {
      setActiveDetailTag(null);
    }
  }, [filteredSortedRows, activeDetailTag]);

  const detailRow = useMemo(() => {
    if (!activeDetailTag) return null;
    return sortedRows.find((r) => r.tag === activeDetailTag) || null;
  }, [sortedRows, activeDetailTag]);

  const handleSortHeaderClick = useCallback((key) => {
    setTagStatsPage(0);
    setSortKey((prevKey) => {
      if (prevKey === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prevKey;
      }
      setSortDir(key === "count" || key === "avgPopularity" ? "desc" : "asc");
      return key;
    });
    setActiveDetailTag(null);
  }, []);

  const selectTag = useCallback((tag) => {
    setActiveDetailTag((prev) => (prev === tag ? null : tag));
  }, []);

  const sortIndicator = (key) => {
    if (sortKey !== key) return "";
    return sortDir === "asc" ? " ▲" : " ▼";
  };

  const handleOverlayMouseDown = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="settings-modal-overlay tag-stats-modal-overlay"
      onMouseDown={handleOverlayMouseDown}
      role="presentation"
    >
      <div
        className="settings-modal-content wide"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Hashtag statistics"
      >
        <div className="settings-modal-header">
          <h2>Hashtag statistics</h2>
          <div className="settings-header-actions">
            <button className="settings-close-button" type="button" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
        <p className="tag-stats-sub">
          Statistics are built in the background when this window opens (the library stays responsive). Only{" "}
          {TAG_STATS_TABLE_PAGE_SIZE} tag rows render per page; use Filter tags and Prev/Next. Sort column headers as
          before. Click a row for questions below; long lists use Load more in batches of {TAG_STATS_EXPAND_PAGE}.
        </p>
        {statsLoading ? (
          <p className="tag-stats-loading">Computing hashtag statistics…</p>
        ) : tagRows.length === 0 ? (
          <p className="tag-stats-empty">No hashtags found in the loaded library (or no questions loaded yet).</p>
        ) : (
          <>
            <div className="tag-stats-filter-row">
              <label htmlFor="tagStatsFilter">Filter tags</label>
              <input
                id="tagStatsFilter"
                type="search"
                className="tag-stats-filter-input"
                value={tagNameFilter}
                onChange={(e) => {
                  setTagNameFilter(e.target.value);
                  setTagStatsPage(0);
                }}
                placeholder="e.g. waylon"
                spellCheck={false}
              />
            </div>
            <table className="tag-stats-table">
              <thead>
                <tr>
                  <th className="tag-stats-th-sortable" scope="col">
                    <button type="button" className="tag-stats-sort-btn" onClick={() => handleSortHeaderClick("tag")}>
                      Tag{sortIndicator("tag")}
                    </button>
                  </th>
                  <th className="tag-stats-th-sortable" scope="col">
                    <button type="button" className="tag-stats-sort-btn" onClick={() => handleSortHeaderClick("count")}>
                      Count{sortIndicator("count")}
                    </button>
                  </th>
                  <th className="tag-stats-th-sortable" scope="col">
                    <button
                      type="button"
                      className="tag-stats-sort-btn"
                      onClick={() => handleSortHeaderClick("avgPopularity")}
                    >
                      Avg interest{sortIndicator("avgPopularity")}
                    </button>
                  </th>
                  <th className="tag-stats-th-sortable" scope="col">
                    <button
                      type="button"
                      className="tag-stats-sort-btn"
                      onClick={() => handleSortHeaderClick("predominantGenre")}
                    >
                      Predominant genre{sortIndicator("predominantGenre")}
                    </button>
                  </th>
                  <th className="tag-stats-th-sortable" scope="col">
                    <button
                      type="button"
                      className="tag-stats-sort-btn"
                      onClick={() => handleSortHeaderClick("predominantCategory")}
                    >
                      Predominant category{sortIndicator("predominantCategory")}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageSlice.map((row) => (
                  <TagStatsTableRow
                    key={row.tag}
                    tag={row.tag}
                    count={row.count}
                    avgPopularity={row.avgPopularity}
                    predominantGenre={row.predominantGenre}
                    predominantCategory={row.predominantCategory}
                    isActive={activeDetailTag === row.tag}
                    onSelect={selectTag}
                  />
                ))}
              </tbody>
            </table>
            <div className="tag-stats-pager">
              <button
                type="button"
                className="tag-stats-pager-btn"
                disabled={tagStatsPage <= 0}
                onClick={() => setTagStatsPage((p) => Math.max(0, p - 1))}
              >
                Prev
              </button>
              <span className="tag-stats-pager-meta">
                Page {tagStatsPage + 1} of {totalTagPages} — {filteredSortedRows.length} tag
                {filteredSortedRows.length !== 1 ? "s" : ""}
              </span>
              <button
                type="button"
                className="tag-stats-pager-btn"
                disabled={tagStatsPage >= totalTagPages - 1}
                onClick={() => setTagStatsPage((p) => Math.min(totalTagPages - 1, p + 1))}
              >
                Next
              </button>
            </div>
            {detailRow && (
              <div className="tag-stats-detail-panel" role="region" aria-label={`Questions for ${detailRow.tag}`}>
                <div className="tag-stats-detail-heading">
                  Questions for #{detailRow.tag} ({detailRow.count})
                </div>
                <TagExpandedQuestionList itemIds={detailRow.items} questionById={questionById} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});
// --- END REPLACE ---
// --- END REPLACE ---
// --- END REPLACE ---

const SliderInput = ({ label, value, onChange, min = 0, max = 100 }) => (
  <div className="detail-item">
    <label>{label} ({value || 0})</label>
    <input type="range" min={min} max={max} value={value || 0} onChange={(e) => onChange(Number(e.target.value))} className="slider" />
  </div>
);

// --- CONSTANTS ---

const categoryStyles = {
  MULTICHOICE: { color: "#fff", background: "#198754" },
  NEARESTANSWER: { color: "#fff", background: "#6f42c1" },
  JACKPOT: { color: "#fff", background: "#d4af37" }, // Gold color for jackpot theme
  VANISHINGCHOICE: { color: "#fff", background: "#0d6efd" },
  MULTIANSWER: { color: "#fff", background: "#fd7e14" },
  TYPE: { color: "#fff", background: "#17a2b8" },
  WHEEL: { color: "#fff", background: "#0d47a1" },
  OUTOFORDER: { color: "#111", background: "#FFDAB9" },
  BUZZER: { color: "#fff", background: "#d81b60" },
  BETANSWER: { color: "#111", background: "#ffb300" },
  'TRUE/FALSE': { color: "#fff", background: "#7e3ce9" },
  DEFAULT: { color: "#000", background: "#f8f9fa" },
};

const ALLOWED_GENRE_STATS = new Set([
  "History", "Science", "Geography", "Famous People", "Music",
  "Pop Culture", "Sport", "Weird", "Wordplay", "Movie",
  "Food and Drink", "TV", "Literature", "Art", "Games",
  "Psychology", "Pop Culture/Weird"
]);

const genreOptions = ["Art", "Famous People", "Food and Drink", "Games", "Geography", "History", "Jokes", "Leisure", "Literature", "Movie", "Music", "Pop Culture", "Psychology", "Science", "Sport", "TV", "Travel", "Weird", "Wordplay"];
const questionTypeOptions = ["MULTICHOICE", "VANISHINGCHOICE", "MULTIANSWER", "NEARESTANSWER", "JACKPOT", "TYPE", "BREADCRUMBS", "OUTOFORDER", "WHEEL", "BUZZER", "BETANSWER", "TRUE/FALSE"];
const LEGACY_VENUES = ["Brendale Brewing", "The Rose and Crown", "Trove", "Red Truck Farm"];

const NEW_QUESTION_TEMPLATE = {
  question: "",
  answer: "",
  A2: "",
  A3: "",
  A4: "",
  A5: "",
  A6: "",
  A7: "",
  A8: "",
  A9: "",
  A10: "",
  A11: "",
  questionType: "MULTICHOICE",
  genre: "Pop Culture",
  difficulty: 50,
  interestRating: 50,
  minAge: 18,
  maxAge: 100,
  dateAdded: new Date().toISOString(),
  Audiolink: null,
  SpotifyUrl: null,
  visibleExplanation: "",
  // No 'id' property
};

function normalizeAccuracyFlag(value) {
  if (value == null) return "";
  const cleaned = String(value).replace(/\s+/g, " ").trim();
  return cleaned;
}

// --- MAIN COMPONENT ---

export default function LibraryViewer() {
  // Filter by the host's own venues, plus the original shared ones so older history stays findable.
  const { venues: hostVenues } = useHostVenues();
  const venueOptions = [...new Set([...hostVenues, ...LEGACY_VENUES]), "Test Venue"];
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tagStatsOpen, setTagStatsOpen] = useState(false);
  /** Frozen copy of QuestionLibrary when hashtag stats opens — parent `questions` updates won't re-scan the whole library. */
  const [tagStatsSnapshot, setTagStatsSnapshot] = useState(null);
  const handleCloseTagStats = useCallback(() => {
    setTagStatsOpen(false);
    setTagStatsSnapshot(null);
  }, []);
  const handleOpenTagStats = useCallback(() => {
    setTagStatsSnapshot(questions);
    setSettingsOpen(false);
    setTagStatsOpen(true);
  }, [questions]);
  const [currentUser, setCurrentUser] = useState(null);
  const [editData, setEditData] = useState(null);
  const [tagsInput, setTagsInput] = useState("");
  const [message, setMessage] = useState("");
  const [sortField, setSortField] = useState(DEFAULT_SORT_FIELD);
  const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION);
  const [mediaPopupUrl, setMediaPopupUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const [validationWarning, setValidationWarning] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [showUsageHistoryPopup, setShowUsageHistoryPopup] = useState(false);
  const [answerStatsByKey, setAnswerStatsByKey] = useState({});
  const [answerStatsLoading, setAnswerStatsLoading] = useState(false);
  const [nearestAnswerChart, setNearestAnswerChart] = useState(null);


  const originalData = useRef(null);
  const fileInputRef = useRef(null);
const [audioPopupUrl, setAudioPopupUrl] = useState(null);
  const audioFileInputRef = useRef(null);
  const audioPlayerRef = useRef(null); // For autoplay of MP3
  const spotifyIframeRef = useRef(null); // For Spotify iframe
  const spotifyPreviewAudioRef = useRef(null); // For Spotify preview autoplay
  const [spotifyPreviewUrl, setSpotifyPreviewUrl] = useState(null);

  const csvFileInputRef = useRef(null);
  const [csvTracks, setCsvTracks] = useState([]);
  const [csvFileName, setCsvFileName] = useState("");
  const [csvError, setCsvError] = useState("");
  const [showCsvUploader, setShowCsvUploader] = useState(false);
  const [usedCsvPreviewUrls, setUsedCsvPreviewUrls] = useState([]);
  const [bobModalMessage, setBobModalMessage] = useState("");
  const csvTrackRefs = useRef({});
  const [csvSortConfig, setCsvSortConfig] = useState({
    key: "trackName",
    direction: "asc"
  });
  const [audioJustUploaded, setAudioJustUploaded] = useState(false);
  const [chosenCsvTrackUrl, setChosenCsvTrackUrl] = useState("");

const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });

  const handleResetSettings = () => {
    setFilters({ ...DEFAULT_FILTERS });
    setSearchTerm("");
    setSortField(DEFAULT_SORT_FIELD);
    setSortDirection(DEFAULT_SORT_DIRECTION);
  };

  const usedCsvPreviewUrlSet = useMemo(
    () => new Set(usedCsvPreviewUrls.map(normalizeUrlKey)),
    [usedCsvPreviewUrls]
  );
  const visibleCsvTracks = useMemo(
    () =>
      csvTracks.filter(
        (track) => !usedCsvPreviewUrlSet.has(normalizeUrlKey(track.previewUrl))
      ),
    [csvTracks, usedCsvPreviewUrlSet]
  );

  const sortedCsvTracks = useMemo(() => {
    const tracks = [...visibleCsvTracks];
    const { key, direction } = csvSortConfig;
    const dir = direction === "desc" ? -1 : 1;
    tracks.sort((a, b) => {
      const aVal = (a?.[key] || "").toString().toLowerCase();
      const bVal = (b?.[key] || "").toString().toLowerCase();
      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
      return 0;
    });
    return tracks;
  }, [visibleCsvTracks, csvSortConfig]);

  const { suggestedCsvPreviewUrl, suggestedCsvPreviewUrlSet } = useMemo(() => {
    const empty = { suggestedCsvPreviewUrl: "", suggestedCsvPreviewUrlSet: new Set() };
    if (!editData || visibleCsvTracks.length === 0) return empty;
    const questionText = collectQuestionText(editData);
    const questionTokens = new Set(
      tokenizeText(questionText).filter((t) => !STOP_WORDS.has(t))
    );
    if (questionTokens.size === 0) return empty;

    let bestUrl = "";
    let bestScore = 0;
    const matchingUrls = new Set();
    visibleCsvTracks.forEach((track) => {
      const score = scoreTrackMatch(track, questionTokens, questionText);
      if (score > 0) {
        const key = normalizeUrlKey(track.previewUrl);
        if (key) matchingUrls.add(key);
        if (score > bestScore) {
          bestScore = score;
          bestUrl = track.previewUrl;
        }
      }
    });

    return {
      suggestedCsvPreviewUrl: bestScore > 0 ? bestUrl : "",
      suggestedCsvPreviewUrlSet: matchingUrls
    };
  }, [editData, visibleCsvTracks]);

  useEffect(() => {
    if (!showCsvUploader || !suggestedCsvPreviewUrl) return;
    const key = normalizeUrlKey(suggestedCsvPreviewUrl);
    const target = csvTrackRefs.current[key];
    if (target?.scrollIntoView) {
      target.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [showCsvUploader, suggestedCsvPreviewUrl, visibleCsvTracks.length]);

  useEffect(() => {
    const stored = localStorage.getItem(CSV_TRACK_STORAGE_KEY);
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed?.tracks)) {
        setCsvTracks(parsed.tracks);
        setCsvFileName(parsed.fileName || "");
      }
    } catch (error) {
      console.warn("[LibraryViewer] Failed to parse stored CSV tracks:", error);
    }
  }, []);

// --- UI COMPONENTS ---

// ... BobPopup, ConfirmationPopup, MediaPopup, etc. ...

// --- NEW USAGE HISTORY POPUP ---
// --- NEW USAGE HISTORY POPUP (Corrected Structure) ---
const UsageHistoryPopup = ({ history, questionId, onDeleteEntry, onClose }) => {
  // Define sortedHistory unconditionally. Sort only if history is valid.
  const sortedHistory = (Array.isArray(history) && history.length > 0)
    ? [...history].sort((a, b) => {
        const dateA = a.usedAt?.toDate ? a.usedAt.toDate() : new Date(a.usedAt || 0);
        const dateB = b.usedAt?.toDate ? b.usedAt.toDate() : new Date(b.usedAt || 0);
        return dateB - dateA; // Newest first
      })
    : []; // Default to empty array if history is invalid or empty

  // Determine if there's content to show
  const hasHistory = sortedHistory.length > 0;

  return (
    <div className="bob-popup-overlay" onClick={onClose}>
      <div
        className={hasHistory ? "usage-history-popup-content" : "bob-popup-content"} // Use different class for styling if needed
        onClick={e => e.stopPropagation()}
      >
        {hasHistory ? (
          // Render the table if there is history
          <>
<h2>Question Usage History</h2>
            <div className="usage-history-table-container">
              <table className="usage-history-table">
                <thead>
                  <tr>
                    <th>Date Used</th>
                    <th>Game ID</th>
                    <th>Host ID</th>
                    {/* --- CHANGE: Header to "Venue" --- */}
                    <th>Venue</th>
                    {/* --- END CHANGE --- */}
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedHistory.map((entry, index) => {
                    // Safely format date
                    let usedDateStr = "Invalid Date";
                    try {
                      const dateObj = entry.usedAt?.toDate ? entry.usedAt.toDate() : new Date(entry.usedAt || '');
                      if (!isNaN(dateObj)) {
                        usedDateStr = dateObj.toLocaleString(); // Use local time format
                      }
                    } catch (e) { console.error("Error parsing date:", entry.usedAt, e); }

                    // --- CHANGE: Extract Venue ---
                    const venueDisplay = entry.location?.venueId ||
                                         (typeof entry.location === 'string' ? entry.location : '-') || // Fallback if old string format exists
                                         '-'; // Default fallback
                    // --- END CHANGE ---

                    return (
                      <tr key={`${entry.gameId}-${index}`}>
                        <td>{usedDateStr}</td>
                        {/* Make Game ID and Host ID slightly smaller if needed */}
                        <td style={{ fontSize: '0.85em', wordBreak: 'break-all' }}>{entry.gameId || '-'}</td>
                        <td style={{ fontSize: '0.85em', wordBreak: 'break-all' }}>{entry.hostId || '-'}</td>
                        {/* --- CHANGE: Display Venue --- */}
                        <td>{venueDisplay}</td>
                        {/* --- END CHANGE --- */}
                        <td>
                          {/* --- CHANGE: Button Styling --- */}
                          <button
                            className="delete-history-btn"
                            onClick={() => onDeleteEntry(questionId, entry)}
                            // Make button smaller
                            style={{ padding: '2px 6px', fontSize: '0.75rem' }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button onClick={onClose} style={{ marginTop: '20px', alignSelf: 'center', padding: '10px 25px' }}>
              Close
            </button>
          </>
        ) : (
          // Render the "not used yet" message if history is empty
          <>
            <div className="bob-popup-header">Question Usage History</div>
            <p>This question has not been used yet.</p>
            <button onClick={onClose}>Close</button>
          </>
        )}
      </div>
    </div>
  );
};
// --- END REPLACEMENT ---

  

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => setCurrentUser(user));
    return () => unsubscribe();
  }, []);

  // Handle questionId URL parameter to open specific question
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const questionIdParam = urlParams.get('questionId');
    if (questionIdParam && questions.length > 0) {
      // Find the question index in the filtered questions
      const questionIndex = filteredQuestions.findIndex(q => q.id === questionIdParam);
      if (questionIndex >= 0) {
        setActiveIndex(questionIndex);
      } else {
        // If not in filtered, try to find in all questions and set filters to show it
        const questionIndexAll = questions.findIndex(q => q.id === questionIdParam);
        if (questionIndexAll >= 0) {
          const question = questions[questionIndexAll];
          // Set filters to match this question so it appears in filtered list
          if (question.genre) setFilters(prev => ({ ...prev, genreFilter: question.genre }));
          if (question.questionType) setFilters(prev => ({ ...prev, categoryFilter: question.questionType }));
          // After filters update, the question should appear in filteredQuestions
          // We'll set activeIndex in a follow-up effect
        }
      }
    }
  }, [questions, filteredQuestions]);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "QuestionLibrary"), orderBy(sortField, sortDirection));
      const querySnapshot = await getDocs(q);
      setQuestions(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error("Error fetching questions:", error);
      setMessage(`Error: ${error.message}`);
    }
    setLoading(false);
  }, [sortField, sortDirection]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  useEffect(() => {
    let filtered = questions;
    const hasMusicAttachment = (q) => {
      const audioLink = (q?.Audiolink ?? "").toString().trim();
      const spotifyUrl = (q?.SpotifyUrl ?? "").toString().trim();
      return audioLink !== "" || spotifyUrl !== "";
    };
    
    // --- FLAGGED FILTER (changed from checkbox to dropdown) ---
    if (filters.flaggedFilter === "Flagged") {
      filtered = filtered.filter(q => q.accuracyFlag && String(q.accuracyFlag).trim() !== '');
    } else if (filters.flaggedFilter === "Unflagged") {
      filtered = filtered.filter(q => !q.accuracyFlag || String(q.accuracyFlag).trim() === '');
    }
    // "All" case - no filtering needed
    // --- END FLAGGED FILTER ---

    // --- MUSIC FILTER ---
    if (filters.musicFilter === "WithMusic") {
      filtered = filtered.filter(q => hasMusicAttachment(q));
    } else if (filters.musicFilter === "WithoutMusic") {
      filtered = filtered.filter(q => !hasMusicAttachment(q));
    }
    // "All" case - no filtering needed
    // --- END MUSIC FILTER ---

    // --- GENRE FILTER ---
    if (filters.genreFilter) {
      filtered = filtered.filter(q => q.genre === filters.genreFilter);
    }
    // --- END GENRE FILTER ---

    // --- CATEGORY FILTER ---
    if (filters.categoryFilter) {
      filtered = filtered.filter(q => q.questionType === filters.categoryFilter);
    }
    // --- END CATEGORY FILTER ---

    // --- VENUE FILTER (used/unused at selected venue) ---
    if (filters.venueFilter && filters.venueUsageFilter !== "All") {
      filtered = filtered.filter(q => {
        const usageHistory = q.usageHistory || [];
        const hasVenueUsage = usageHistory.some(entry => {
          const entryVenue = entry.location?.venueId ||
                            (typeof entry.location === 'string' ? entry.location : null);
          return entryVenue === filters.venueFilter;
        });

        return filters.venueUsageFilter === "Unused" ? !hasVenueUsage : hasVenueUsage;
      });
    }
    // --- END VENUE FILTER ---

    // --- HASHTAG FILTER ---
    const normalizedHashtagFilters = extractTagTokens(filters.hashtagFilter)
      .map(tag => normalizeHashtag(tag))
      .filter(Boolean);
    if (normalizedHashtagFilters.length > 0) {
      filtered = filtered.filter(q => {
        const normalizedTags = extractTagTokens(q?.tags)
          .map(tag => normalizeHashtag(tag))
          .filter(Boolean);
        return normalizedHashtagFilters.every(tag => normalizedTags.includes(tag));
      });
    }
    // --- END HASHTAG FILTER ---

    // --- SEARCH FILTER ---
    if (searchTerm.trim() !== '') {
        const lowercasedTerm = searchTerm.toLowerCase();
        filtered = filtered.filter(q => {
            const searchFields = [q.question, q.answer, q.A2, q.A3, q.A4, q.A5, q.A6, q.A7, q.A8, q.A9, q.A10, q.A11];
            return searchFields.some(field => field && typeof field === 'string' && field.toLowerCase().includes(lowercasedTerm));
        });
    }
    // --- END SEARCH FILTER ---
    
    // --- DEDUPLICATION SAFETY CHECK ---
    // Remove any duplicate questions by ID (safety net against edge cases)
    const seenIds = new Set();
    const deduplicated = filtered.filter(q => {
      if (!q.id) {
        // Questions without IDs should not appear in filtered list
        return false;
      }
      if (seenIds.has(q.id)) {
        console.warn(`Duplicate question detected in filteredQuestions: ${q.id}. Removing duplicate.`);
        return false;
      }
      seenIds.add(q.id);
      return true;
    });
    // --- END DEDUPLICATION ---
    
    setFilteredQuestions(deduplicated);
  }, [questions, filters, searchTerm]);

  useEffect(() => { 
    if (isCreatingNew) {
      // Don't reset index or data if we are in "create new" mode
      return;
    }
    
    // Check if we have a questionId in URL and handle it
    const urlParams = new URLSearchParams(window.location.search);
    const questionIdParam = urlParams.get('questionId');
    if (questionIdParam && filteredQuestions.length > 0) {
      const questionIndex = filteredQuestions.findIndex(q => q.id === questionIdParam);
      if (questionIndex >= 0 && activeIndex !== questionIndex) {
        setActiveIndex(questionIndex);
        return;
      }
    }
    
    // Only reset index if it's out of bounds or was -1 (and no questionId in URL)
    if ((activeIndex === -1 || activeIndex >= filteredQuestions.length) && !questionIdParam) {
      setActiveIndex(filteredQuestions.length > 0 ? 0 : -1);
    }
  }, [filters, searchTerm, filteredQuestions, activeIndex, isCreatingNew]);
  
  useEffect(() => {
    if (isCreatingNew) {
      // When creating new, editData is set manually by handleAddClick
      return;
    }
    if (activeIndex >= 0 && activeIndex < filteredQuestions.length) {
      const currentQuestion = filteredQuestions[activeIndex];
      setEditData(currentQuestion ? { ...currentQuestion } : null);
      originalData.current = currentQuestion ? { ...currentQuestion } : null;
    } else {
      setEditData(null); // No questions to show
      originalData.current = null;
    }
  }, [activeIndex, filteredQuestions, isCreatingNew]);

  // Construct and verify Spotify preview URL when Spotify URL is available
  useEffect(() => {
    if (editData?.SpotifyUrl && !editData?.Audiolink) {
      const trackId = extractSpotifyTrackId(editData.SpotifyUrl);
      console.log('[LibraryViewer] Extracted track ID:', trackId, 'from URL:', editData.SpotifyUrl);
      if (trackId) {
        const previewUrl = getSpotifyPreviewUrl(trackId);
        console.log('[LibraryViewer] Constructed Spotify preview URL:', previewUrl);
        
        // Verify the preview URL exists by trying to load it
        const testAudio = new Audio();
        testAudio.src = previewUrl;
        testAudio.preload = 'metadata';
        
        const timeout = setTimeout(() => {
          console.warn('[LibraryViewer] Preview URL verification timeout - will try anyway');
          setSpotifyPreviewUrl(previewUrl);
        }, 2000);
        
        testAudio.addEventListener('loadedmetadata', () => {
          clearTimeout(timeout);
          console.log('[LibraryViewer] Preview URL verified - exists and is accessible');
          setSpotifyPreviewUrl(previewUrl);
        }, { once: true });
        
        testAudio.addEventListener('error', () => {
          clearTimeout(timeout);
          console.warn('[LibraryViewer] Preview URL does not exist for this track - will use iframe only');
          setSpotifyPreviewUrl(null);
        }, { once: true });
      } else {
        console.warn('[LibraryViewer] Could not extract track ID from Spotify URL');
        setSpotifyPreviewUrl(null);
      }
    } else if (!editData?.SpotifyUrl) {
      // Clear preview URL when Spotify URL is removed
      setSpotifyPreviewUrl(null);
    } else if (editData?.Audiolink) {
      // Clear preview URL if MP3 is present (MP3 takes priority)
      setSpotifyPreviewUrl(null);
    }
  }, [editData?.SpotifyUrl, editData?.Audiolink]);

  // Autoplay only right after a new audio upload
  useEffect(() => {
    if (!audioJustUploaded) return;
    if (!editData?.Audiolink) return;

    const audio = audioPlayerRef.current;
    if (!audio) return;

    const attemptPlay = () => {
      console.log('[LibraryViewer Autoplay] Attempting to play MP3:', editData.Audiolink);
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .catch((err) => {
            console.warn('[LibraryViewer Autoplay] MP3 autoplay blocked, trying muted:', err);
            audio.muted = true;
            return audio.play().then(() => {
              setTimeout(() => {
                audio.muted = false;
                console.log('[LibraryViewer Autoplay] MP3 unmuted');
              }, 150);
            });
          })
          .catch((err2) => {
            console.error('[LibraryViewer Autoplay] MP3 play failed even after muting:', err2);
          })
          .finally(() => {
            setAudioJustUploaded(false);
          });
      } else {
        setAudioJustUploaded(false);
      }
    };

    if (audio.readyState >= 2) {
      attemptPlay();
    } else {
      const handleCanPlay = () => attemptPlay();
      audio.addEventListener("canplay", handleCanPlay, { once: true });
      setTimeout(attemptPlay, 500);
    }
  }, [audioJustUploaded, editData?.Audiolink]);






  
  const handleSave = async (dataToSave, isDataChanged) => {
    const { tagsArray, tagsString } = normalizeTagsForSave(dataToSave?.tags, tagsInput);
    if (tagsString !== tagsInput) {
      setTagsInput(tagsString);
    }
    if (Array.isArray(tagsArray) && JSON.stringify(tagsArray) !== JSON.stringify(dataToSave?.tags || [])) {
      dataToSave = { ...dataToSave, tags: tagsArray };
    }
    if (isCreatingNew && !dataToSave.id) {
      // --- This is a NEW question being saved ---
      setMessage("Adding question...");
      try {
        const dataToAdd = { 
          ...dataToSave, 
          accuracyFlag: normalizeAccuracyFlag(dataToSave.accuracyFlag),
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          lastReviewedBy: currentUser?.displayName || currentUser?.email || "Unknown User",
          lastReviewedDate: new Date().toISOString()
        };
        delete dataToAdd.id; // Ensure no 'id' field is sent for addDoc

        const docRef = await addDoc(collection(db, "QuestionLibrary"), dataToAdd);
        const newQuestion = { ...dataToAdd, id: docRef.id, createdAt: new Date().toISOString() };

        // Update state - let the filtering useEffect handle filteredQuestions automatically
        setQuestions(prev => [newQuestion, ...prev]); 
        setIsCreatingNew(false); // We are no longer creating
        originalData.current = { ...newQuestion }; // Sync originalData after successful save
        
        setMessage("Question Added.");
        return newQuestion; // Return the new question
      } catch (error) {
        console.error("Error adding question:", error);
        setMessage(`Error: ${error.message}`);
        setValidationWarning(`Error: ${error.message}`);
        return null;
      }
    } else if (dataToSave.id) {
      // --- This is an EXISTING question being updated ---
      setMessage("Saving...");
      try {
        const questionRef = doc(db, "QuestionLibrary", dataToSave.id);
        let dataWithMeta = { ...dataToSave, accuracyFlag: normalizeAccuracyFlag(dataToSave.accuracyFlag) };
        if (isDataChanged) {
          dataWithMeta = { ...dataWithMeta, lastReviewedBy: currentUser?.displayName || currentUser?.email || "Unknown User", lastReviewedDate: new Date().toISOString() };
        }
        dataWithMeta.updatedAt = serverTimestamp();
        await setDoc(questionRef, dataWithMeta, { merge: true });
        
        // Update local state
        const updatedQuestion = { ...dataWithMeta, updatedAt: new Date().toISOString() };
        setQuestions(prev => prev.map(q => q.id === updatedQuestion.id ? updatedQuestion : q));
        originalData.current = { ...updatedQuestion }; // Sync originalData after successful save
        
        setMessage("Saved.");
        return updatedQuestion;
      } catch (error) {
        console.error("Error saving question:", error);
        setMessage(`Error: ${error.message}`);
        setValidationWarning(`Error: ${error.message}`);
        return null;
      }
    } else {
      // Defensive check: prevent saving existing question without ID
      if (!isCreatingNew && !dataToSave.id) {
        console.error("handleSave: Attempted to save existing question without ID. This should not happen.");
        setMessage("Error: Cannot save question without ID.");
        setValidationWarning("Error: Cannot save question without ID.");
        return null;
      }
      console.warn("handleSave called without ID and not in isCreatingNew mode.");
    }

    setTimeout(() => setMessage(""), 2000);
    return null;
  };
  
  const handleNavigation = async (nextIndex) => {
    if (!editData) return; // Don't navigate if no data
    
    // --- Validation ---
    if (!editData.genre || !genreOptions.includes(editData.genre)) {
      setValidationWarning("Please select a valid Genre before continuing.");
      return;
    }
    if (!editData.questionType || !questionTypeOptions.includes(editData.questionType)) {
      setValidationWarning("Please select a valid Category before continuing.");
      return;
    }
    if ((editData.minAge || 0) > (editData.maxAge || 100)) {
      setValidationWarning("Minimum Age cannot be greater than Maximum Age.");
      return;
    }
    const blankAnswerError = checkForBlankAnswers();
    if (blankAnswerError) {
      setValidationWarning(blankAnswerError);
      return;
    }

    const isSingleAnswerQuestion = ['NEARESTANSWER', 'JACKPOT', 'WHEEL'].includes(editData.questionType);
    if (!isSingleAnswerQuestion && checkForDuplicateAnswers()) {
      setValidationWarning("You have two or more near-identical answers. Please review them before continuing.");
      return;
    }

    if (editData.questionType === 'MULTIANSWER') {
      const correctAnswers = ['answer', 'A2', 'A3', 'A4', 'A5', 'A6'].map(key => editData[key]).filter(val => val && typeof val === 'string' && val.startsWith('[') && val.endsWith(']')).length;
      if (correctAnswers < 2) {
        setValidationWarning("MULTIANSWER questions must have at least two correct answers marked with [].");
        return;
      }
      if (correctAnswers > 4) {
        setValidationWarning("You have more than 4 correct options, consider asking a 'negative' version of this question or making it an OUTOFORDER question?");
        return;
      }
    }
    // --- End Validation ---

    // Defensive check: prevent accidentally creating new question when updating existing
    if (!isCreatingNew && !editData.id) {
      console.error("handleNavigation: Attempted to save existing question without ID. This should not happen.");
      setValidationWarning("Error: Cannot save question without ID. Please refresh and try again.");
      return;
    }

    const { tagsArray, tagsString } = normalizeTagsForSave(editData?.tags, tagsInput);
    const dataToSave = { ...editData, tags: tagsArray };
    if (tagsString !== tagsInput) {
      setTagsInput(tagsString);
    }
    if (JSON.stringify(dataToSave) !== JSON.stringify(editData)) {
      setEditData(dataToSave);
    }
    const hasChanged = JSON.stringify(dataToSave) !== JSON.stringify(originalData.current);
    
    // Only save if there are actual changes
    if (hasChanged) {
      const savedQuestion = await handleSave(dataToSave, hasChanged);

      if (!savedQuestion) {
        // Save failed, don't navigate
        return;
      }

      if (isCreatingNew && savedQuestion) {
        // We just added a question. It's now at index 0 in filteredQuestions.
        // Force the index to 0, regardless of "Prev" or "Next"
        setIsCreatingNew(false); // handleSave should do this, but redundant
        setActiveIndex(0);
        return;
      }
    }

    // If no changes, or save succeeded, navigate normally
    setIsCreatingNew(false); // Not creating new anymore
    setActiveIndex(nextIndex);
  };

  const checkForBlankAnswers = () => {
    if (!editData) return null;
    const category = (editData.questionType || "").toUpperCase();
    const answerKeys = category === "BREADCRUMBS" ? BREADCRUMBS_ANSWER_KEYS : ['answer', 'A2', 'A3', 'A4', 'A5', 'A6'];
    const answers = answerKeys.map(key => (editData[key] || '').trim());
    if (answers[0] === '') return "The main 'Answer' field cannot be left blank.";
    if (category === "BREADCRUMBS") {
      const hasClue = answers.slice(1).some(a => a !== '');
      if (!hasClue) return "BREADCRUMBS requires at least one clue (A2–A11).";
      return null;
    }
    let lastAnswerIndex = -1;
    for (let i = answers.length - 1; i > 0; i--) {
        if (answers[i] !== '') {
            lastAnswerIndex = i;
            break;
        }
    }
    if (lastAnswerIndex > 0) {
        for (let i = 1; i < lastAnswerIndex; i++) {
            if (answers[i] === '') return `Answer field ${answerKeys[i].toUpperCase()} is blank, but subsequent fields are filled. Please fill in the gaps or clear the later answers.`;
        }
    }
    return null;
  };

  const checkForDuplicateAnswers = () => {
    if (!editData) return false;
    const category = (editData.questionType || "").toUpperCase();
    if (category === "BREADCRUMBS") return false;
    const answers = ['answer', 'A2', 'A3', 'A4', 'A5', 'A6'].map(key => editData[key]).filter(val => val && typeof val === 'string' && val.trim() !== '');
    for (let i = 0; i < answers.length; i++) {
      for (let j = i + 1; j < answers.length; j++) {
        if (calculateSimilarity(answers[i], answers[j]) > 0.9) return true;
      }
    }
    return false;
  };

  const handlePrev = () => {
    if (filteredQuestions.length === 0 && !isCreatingNew) return;
    const nextIndex = activeIndex > 0 ? activeIndex - 1 : filteredQuestions.length - 1;
    handleNavigation(nextIndex);
  };
  const handleNext = () => {
    if (filteredQuestions.length === 0 && !isCreatingNew) return;
    const nextIndex = activeIndex < filteredQuestions.length - 1 ? activeIndex + 1 : 0;
    handleNavigation(nextIndex);
  };

  const handleAddClick = () => {
    const retainedCategory = editData?.questionType || NEW_QUESTION_TEMPLATE.questionType;
    const retainedGenre = editData?.genre || NEW_QUESTION_TEMPLATE.genre;

    // If we're already creating new, just reset the form
    if (isCreatingNew) {
      const resetTemplate = {
        ...NEW_QUESTION_TEMPLATE,
        questionType: retainedCategory,
        genre: retainedGenre,
        maxAge: 100,
        dateAdded: new Date().toISOString()
      };
      setEditData(resetTemplate);
      originalData.current = { ...resetTemplate };
      return;
    }

    // If we are editing an existing question, save it first.
    if (editData) {
      const { tagsArray, tagsString } = normalizeTagsForSave(editData?.tags, tagsInput);
      const dataToSave = { ...editData, tags: tagsArray };
      if (tagsString !== tagsInput) {
        setTagsInput(tagsString);
      }
      if (JSON.stringify(dataToSave) !== JSON.stringify(editData)) {
        setEditData(dataToSave);
      }
      const hasChanged = JSON.stringify(dataToSave) !== JSON.stringify(originalData.current);
      if (editData?.id) handleSave(dataToSave, hasChanged);
    }
    
    const newQuestionTemplate = {
      ...NEW_QUESTION_TEMPLATE,
      questionType: retainedCategory,
      genre: retainedGenre,
      maxAge: 100,
      dateAdded: new Date().toISOString(),
      lastReviewedBy: currentUser?.displayName || currentUser?.email || "Unknown User",
      lastReviewedDate: new Date().toISOString(),
    };
    
    setIsCreatingNew(true);
    setActiveIndex(-1); // This will clear the index
    setEditData(newQuestionTemplate);
    originalData.current = { ...newQuestionTemplate };
  };

  const handleConfirmDelete = async () => {
    if (!editData?.id || isCreatingNew) return;
    
    const docIdToDelete = editData.id;
    const mediaLinkToDelete = editData.Medialink;
    const audioLinkToDelete = editData.Audiolink;

    setShowDeleteConfirm(false);
    setMessage("Deleting...");

    try {
      // 1. Delete media from Storage, if it exists
      if (mediaLinkToDelete) {
        try {
          await deleteObject(ref(storage, mediaLinkToDelete));
        } catch (storageError) {
          console.warn("Could not delete file from storage, continuing with doc delete:", storageError.message);
        }
      }


      // 1b. Delete audio from Storage, if it exists
      if (audioLinkToDelete) {
        try {
          await deleteObject(ref(storage, audioLinkToDelete));
        } catch (storageError) {
          console.warn("Could not delete audio file from storage, continuing with doc delete:", storageError.message);
        }
      }
      
      // 2. Delete document from Firestore
      await deleteDoc(doc(db, "QuestionLibrary", docIdToDelete));

      // 3. Update local 'questions' state, which triggers 'filteredQuestions' update
      setQuestions(prev => prev.filter(q => q.id !== docIdToDelete));
      
      // useEffects will handle recalculating the filtered list and setting a new activeIndex/editData
      
      setMessage("Question deleted.");
    } catch (error) {
      console.error("Error deleting question:", error);
      setMessage(`Error: ${error.message}`);
    }
    setTimeout(() => setMessage(""), 2000);
  };

  const handleInputChange = (field, value) => {
    const category = (editData?.questionType || "").toUpperCase();
    const isBreadcrumbsClue = category === "BREADCRUMBS" && BREADCRUMBS_CLUE_KEYS.includes(field);
    const maxLen = isBreadcrumbsClue ? BREADCRUMBS_CLUE_MAX_LENGTH : ANSWER_MAX_LENGTH;
    const isAnswerField = ANSWER_KEYS.includes(field) || (category === "BREADCRUMBS" && BREADCRUMBS_ANSWER_KEYS.includes(field));
    if (isAnswerField && typeof value === "string" && value.length > maxLen) {
      const nextValue = value.startsWith("[") && value.endsWith("]")
        ? `[${value.slice(1, -1).slice(0, maxLen - 2)}]`
        : value.slice(0, maxLen);
      setEditData(prev => ({ ...prev, [field]: nextValue }));
      setBobModalMessage(isBreadcrumbsClue
        ? `Clue text is limited to ${BREADCRUMBS_CLUE_MAX_LENGTH} characters.`
        : `Answer text is limited to ${ANSWER_MAX_LENGTH} characters.`);
      return;
    }
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleOutOfOrderDragEnd = (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    setEditData((prev) => {
      if (!prev) return prev;
      const keys = ANSWER_KEYS.filter(
        (k) => prev[k] != null && String(prev[k] ?? "").trim() !== ""
      );
      if (keys.length < 2) return prev;
      const nextVals = reorderListImmutable(
        keys.map((k) => prev[k]),
        result.source.index,
        result.destination.index
      );
      const next = { ...prev };
      keys.forEach((k, i) => {
        next[k] = nextVals[i];
      });
      return next;
    });
  };

  const handleArrayChange = (field, value) => handleInputChange(field, value.split(',').map(item => item.trim()).filter(Boolean));

  const normalizeTagsForSave = (rawTags, rawTagsInput) => {
    const rawString = typeof rawTagsInput === "string" && rawTagsInput.trim()
      ? rawTagsInput
      : (Array.isArray(rawTags) ? rawTags.join(" ") : (rawTags || ""));
    const hashtagMatches = String(rawString).match(/#[^\s#,]+/g);
    const tokens = hashtagMatches && hashtagMatches.length > 0
      ? hashtagMatches
      : String(rawString)
        .split(/[,\s]+/g)
        .map((item) => item.trim())
        .filter(Boolean);
    const tagsArray = tokens;
    const tagsString = tagsArray.join(", ");
    return { tagsArray, tagsString };
  };

  useEffect(() => {
    if (!editData) {
      setTagsInput("");
      return;
    }
    const nextTags = Array.isArray(editData.tags) ? editData.tags.join(", ") : (editData.tags || "");
    setTagsInput(nextTags);
  }, [activeIndex, editData?.id]);

  useEffect(() => {
    setChosenCsvTrackUrl("");
  }, [editData?.id]);


// --- REPLACE THIS FUNCTION ---
  const handleDeleteUsageEntry = async (qId, entryToDelete) => {
    if (!qId || !entryToDelete) return;

    setMessage("Deleting history entry...");
    setValidationWarning(null); // Clear previous errors

    try {
      const questionRef = doc(db, "QuestionLibrary", qId);

      // --- Robust Delete Logic ---
      // 1. Get the current document data
      const docSnap = await getDoc(questionRef);
      if (!docSnap.exists()) {
        throw new Error("Question document not found.");
      }
      const currentData = docSnap.data();
      const currentHistory = currentData.usageHistory || [];

      // 2. Filter out the entry to delete
      // We need a reliable way to identify the entry. Let's use gameId and timestamp.
      // Convert Firestore Timestamps to milliseconds for comparison.
      const entryToDeleteMs = entryToDelete.usedAt?.toDate ? entryToDelete.usedAt.toDate().getTime() : new Date(entryToDelete.usedAt || 0).getTime();
      const gameIdToDelete = entryToDelete.gameId;

      const updatedHistory = currentHistory.filter(entry => {
        const entryMs = entry.usedAt?.toDate ? entry.usedAt.toDate().getTime() : new Date(entry.usedAt || 0).getTime();
        const entryGameId = entry.gameId;
        // Keep entry if gameId OR timestamp doesn't match
        return !(entryGameId === gameIdToDelete && entryMs === entryToDeleteMs);
      });

      // Check if anything was actually filtered (sanity check)
      if (updatedHistory.length === currentHistory.length) {
         console.warn("Delete failed: Could not find matching entry in Firestore data.", entryToDelete);
         // Attempt direct arrayRemove as a fallback, just in case object comparison works
         try {
             await updateDoc(questionRef, { usageHistory: arrayRemove(entryToDelete) });
             console.log("Fallback arrayRemove attempted.");
             // Re-fetch to confirm if fallback worked
             const checkSnap = await getDoc(questionRef);
             const checkHistory = checkSnap.data()?.usageHistory || [];
             if (checkHistory.length < currentHistory.length) {
                 // Fallback worked, update state
                 setEditData(prev => ({ ...prev, usageHistory: checkHistory }));
                 originalData.current = { ...originalData.current, usageHistory: checkHistory };
                 setQuestions(prev => prev.map(q => q.id === qId ? { ...q, usageHistory: checkHistory } : q));
                 setMessage("History entry deleted (via fallback).");
                 setTimeout(() => setMessage(""), 2000);
                 return; // Exit early
             } else {
                 throw new Error("Fallback arrayRemove also failed to find match.");
             }
         } catch (fallbackError) {
             throw new Error(`Entry not found for deletion. Timestamps might differ slightly. ${fallbackError.message}`);
         }
      }

      // 3. Overwrite the usageHistory field with the filtered array
      await updateDoc(questionRef, {
        usageHistory: updatedHistory
      });
      // --- End Robust Delete Logic ---

      // Update local state immediately with the successfully filtered array
      setEditData(prev => ({ ...prev, usageHistory: updatedHistory }));
      originalData.current = { ...originalData.current, usageHistory: updatedHistory }; // Update original data snapshot if needed
      setQuestions(prev => prev.map(q => q.id === qId ? { ...q, usageHistory: updatedHistory } : q));

      setMessage("History entry deleted.");

    } catch (error) {
      console.error("Error deleting usage entry:", error);
      setMessage(`Error deleting entry: ${error.message}`);
      setValidationWarning(`Error deleting entry: ${error.message}`); // Show in BobPopup
    }
    setTimeout(() => setMessage(""), 2000);
  };
  // --- END REPLACE FUNCTION ---

  
  const handleCategoryChange = (newCategory) => {
    setEditData(prevData => {
        const oldCategory = prevData.questionType;
        let newData = { ...prevData, questionType: newCategory };

        if (oldCategory === newCategory) return prevData;

        const multiAnswerTypes = ['MULTICHOICE', 'VANISHINGCHOICE', 'OUTOFORDER', 'MULTIANSWER', 'BUZZER'];
        const singleAnswerTypes = ['NEARESTANSWER', 'JACKPOT', 'WHEEL'];

        // Switching TO TRUE/FALSE
        if (newCategory === 'TRUE/FALSE') {
            newData.answer = 'TRUE';
            newData.A2 = 'FALSE';
            // Don't null out A3-A6 to preserve data
        } 
        // Switching FROM a single-answer or TRUE/FALSE type to a multi-answer type
        else if (multiAnswerTypes.includes(newCategory) && (singleAnswerTypes.includes(oldCategory) || oldCategory === 'TRUE/FALSE')) {
            ['A2', 'A3', 'A4', 'A5', 'A6'].forEach(key => {
              if (newData[key] === null || newData[key] === undefined) {
                newData[key] = '';
              }
            });
        }
        
        // Switching TO BREADCRUMBS: ensure A7–A11 exist
        if (newCategory === 'BREADCRUMBS') {
            ['A7', 'A8', 'A9', 'A10', 'A11'].forEach(key => {
                if (newData[key] === null || newData[key] === undefined) {
                    newData[key] = '';
                }
            });
        }
        // Switching TO MULTIANSWER
        if (newCategory === 'MULTIANSWER') {
            const answerValue = newData.answer || '';
            if (!answerValue.startsWith('[') || !answerValue.endsWith(']')) {
                newData.answer = `[${answerValue.replace(/[\[\]]/g, '')}]`;
            }
        } 
        // Switching FROM MULTIANSWER
        else if (oldCategory === 'MULTIANSWER') {
            ['answer', 'A2', 'A3', 'A4', 'A5', 'A6'].forEach(key => {
                if (typeof newData[key] === 'string') {
                    newData[key] = newData[key].replace(/[\[\]]/g, '');
                }
            });
        }
        
        return newData;
    });
  };


  const handleCsvFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setCsvError("");

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const normalizedHeaders = (results.meta?.fields || []).map(normalizeHeaderValue);
        const hasPreviewUrlHeader = normalizedHeaders.some((header) =>
          ["trackpreviewurl", "previewurl", "trackpreview"].includes(header)
        );

        if (!hasPreviewUrlHeader) {
          setCsvError("Missing required column: Track Preview URL.");
          event.target.value = "";
          return;
        }

        const rawRows = Array.isArray(results.data) ? results.data : [];
        const mappedRows = rawRows.map(extractCsvTrack).filter((row) => row.previewUrl);
        const seen = new Set();
        const deduped = [];
        mappedRows.forEach((row) => {
          const key = normalizeUrlKey(row.previewUrl);
          if (!key || seen.has(key)) return;
          seen.add(key);
          deduped.push(row);
        });

        if (deduped.length === 0) {
          setCsvError("No rows with Track Preview URL found.");
        }

        setCsvTracks(deduped);
        setCsvFileName(file.name || "");
        localStorage.setItem(
          CSV_TRACK_STORAGE_KEY,
          JSON.stringify({
            fileName: file.name || "",
            tracks: deduped,
            savedAt: new Date().toISOString(),
          })
        );
        event.target.value = "";
      },
      error: (error) => {
        setCsvError(`CSV parse failed: ${error.message}`);
        event.target.value = "";
      },
    });
  };

  const clearCsvTracks = () => {
    setCsvTracks([]);
    setCsvFileName("");
    setCsvError("");
    localStorage.removeItem(CSV_TRACK_STORAGE_KEY);
  };

  const handleCsvTrackUpload = async (track) => {
    if (!track?.previewUrl) return;
    if (!editData) {
      setMessage("Select a question before uploading audio.");
      setTimeout(() => setMessage(""), 2000);
      return;
    }

    setChosenCsvTrackUrl(track.previewUrl);
    setIsUploading(true);
    setMessage("Downloading preview...");
    try {
      const response = await fetch(track.previewUrl);
      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }
      const blob = await response.blob();
      const contentType = response.headers.get("content-type") || blob.type;
      const extension =
        getExtensionFromContentType(contentType) ||
        getExtensionFromUrl(track.previewUrl) ||
        "mp3";
      const baseName = sanitizeFilename(
        track.trackName || track.artistNames || "track"
      );
      const safeName = baseName || "track";
      const fileName = `${safeName}.${extension}`;
      const filePath = `media/audio/${Date.now()}_${fileName}`;
      const storageRef = ref(storage, filePath);
      await uploadBytes(storageRef, blob);
      const downloadURL = await getDownloadURL(storageRef);
      const updatedData = { ...editData, Audiolink: downloadURL };
      setEditData(updatedData);
      setAudioJustUploaded(true);
      await handleSave(updatedData, true);
      setUsedCsvPreviewUrls((prev) => {
        const key = normalizeUrlKey(track.previewUrl);
        if (!key || prev.includes(key)) return prev;
        return [...prev, key];
      });
      setMessage("MP3 upload complete and saved!");
    } catch (error) {
      setMessage(`Upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
      setTimeout(() => setMessage(""), 3000);
    }
  };


const handleAudioFileUpload = async (event) => {
      const file = event.target.files[0];
      if (!file || !editData) return;
      setIsUploading(true);
      setMessage("Uploading audio...");
      try {
          const filePath = `media/audio/${Date.now()}_${file.name}`; // Separate folder
          const storageRef = ref(storage, filePath);
          await uploadBytes(storageRef, file);
          const downloadURL = await getDownloadURL(storageRef);
          const updatedData = { ...editData, Audiolink: downloadURL };
          setEditData(updatedData);
          setAudioJustUploaded(true);
          await handleSave(updatedData, true); 
          setMessage("Audio upload complete and saved!");
      } catch (error) {
          setMessage(`Upload failed: ${error.message}`);
      } finally {
        setIsUploading(false);
        setTimeout(() => setMessage(""), 3000);
      }
  };

  const handleDeleteAudio = async () => {
    if (!editData?.Audiolink) return;
    setMessage("Deleting audio...");
    try {
      await deleteObject(ref(storage, editData.Audiolink));
    } catch (error) { console.warn("Could not delete audio file from storage:", error.message); }
    const updatedData = { ...editData, Audiolink: null };
    setEditData(updatedData);
    await handleSave(updatedData, true);
    setAudioPopupUrl(null); 
    setMessage("Audio deleted.");
    setTimeout(() => setMessage(""), 2000);
  };

  const handleAudioClick = () => {
    if (editData.Audiolink) setAudioPopupUrl(editData.Audiolink);
    else audioFileInputRef.current.click();
  };



  const handleFileUpload = async (event) => {
      const file = event.target.files[0];
      if (!file || !editData) return;
      setIsUploading(true);
      setMessage("Uploading media...");
      try {
          const filePath = `media/${Date.now()}_${file.name}`;
          const storageRef = ref(storage, filePath);
          await uploadBytes(storageRef, file);
          const downloadURL = await getDownloadURL(storageRef);
          const updatedData = { ...editData, Medialink: downloadURL };
          setEditData(updatedData);
          await handleSave(updatedData, true); 
          setMessage("Upload complete and saved!");
      } catch (error) {
          setMessage(`Upload failed: ${error.message}`);
      } finally {
        setIsUploading(false);
        setTimeout(() => setMessage(""), 3000);
      }
  };

  const handleDeleteMedia = async () => {
    if (!editData?.Medialink) return;
    setMessage("Deleting media...");
    try {
      await deleteObject(ref(storage, editData.Medialink));
    } catch (error) { console.warn("Could not delete file from storage:", error.message); }
    const updatedData = { ...editData, Medialink: null };
    setEditData(updatedData);
    await handleSave(updatedData, true);
    setMediaPopupUrl(null); 
    setMessage("Media deleted.");
    setTimeout(() => setMessage(""), 2000);
  };

  const handleMediaClick = () => {
    if (editData.Medialink) setMediaPopupUrl(editData.Medialink);
    else fileInputRef.current.click();
  };

  const handleTrueFalseToggle = () => {
    setEditData(prev => ({
      ...prev,
      answer: prev.A2,
      A2: prev.answer,
    }));
  };

  const dbStats = useMemo(() => {
    const totalQuestions = filteredQuestions.length;
    if (totalQuestions === 0) return { totalQuestions: 0, flaggedCount: 0, categoryInfo: [], genreInfo: [] };
    const flaggedCount = filteredQuestions.filter(q => q.accuracyFlag && String(q.accuracyFlag).trim() !== '').length;
    const categoryCounts = filteredQuestions.reduce((acc, q) => {
      // Check multiple possible field names for question type
      const type = q.questionType || q.type || q.CAT || 'UNCATEGORIZED';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
    const categoryInfo = Object.entries(categoryCounts)
      .map(([type, count]) => ({ type, count, percentage: ((count / totalQuestions) * 100).toFixed(1) }))
      .sort((a, b) => b.count - a.count);


// --- ADD GENRE CALCULATION ---
  const genreCounts = filteredQuestions.reduce((acc, q) => {
    const genre = q.genre || 'UNCATEGORIZED';
    acc[genre] = (acc[genre] || 0) + 1;
    return acc;
  }, {});

  
// Calculate FULL genreInfo first
  const fullGenreInfo = Object.entries(genreCounts)
    .map(([genre, count]) => ({ genre, count, percentage: ((count / totalQuestions) * 100).toFixed(1) }))
    .sort((a, b) => b.count - a.count);

  // --- ADD FILTER STEP ---
  // Filter the list to include only allowed genres
  const genreInfo = fullGenreInfo.filter(info => ALLOWED_GENRE_STATS.has(info.genre));
  // --- END FILTER STEP ---


  

    return { totalQuestions, flaggedCount, categoryInfo, genreInfo };
  }, [filteredQuestions]);

  const category = editData?.questionType || 'DEFAULT';
  const style = categoryStyles[category] || categoryStyles.DEFAULT;

  const singleAnswerTypes = ['NEARESTANSWER', 'JACKPOT', 'WHEEL'];
  const isSingleAnswer = singleAnswerTypes.includes(category);

  const ANSWER_STATS_TYPES = new Set([
    "MULTICHOICE",
    "VANISHINGCHOICE",
    "MULTIANSWER",
    "OUTOFORDER",
    "TRUE/FALSE",
    "BETANSWER",
    "BUZZER",
    "TYPE",
    "BREADCRUMBS",
    "WHEEL",
    "NEARESTANSWER"
  ]);

  const CORRECT_ONLY_TYPES = new Set(["TYPE", "WHEEL", "BREADCRUMBS"]);

  const getAnswerKeysForCategory = (cat) =>
    (cat || "").toUpperCase() === "BREADCRUMBS" ? BREADCRUMBS_ANSWER_KEYS : ANSWER_KEYS;

  const buildAnswerList = () => {
    if (!editData) return [];
    const keys = getAnswerKeysForCategory(editData.questionType);
    return keys.map((key) => ({ key, value: editData[key] }))
      .filter((item) => item.value !== undefined && item.value !== null && String(item.value).trim() !== "");
  };

  const answerStatsEligible = ANSWER_STATS_TYPES.has(category);
  const isCorrectOnlyStats = CORRECT_ONLY_TYPES.has(category);
  const isPositionStats = category === "OUTOFORDER";
  const isNearestAnswerStats = category === "NEARESTANSWER";

  useEffect(() => {
    if (!editData || !editData.id || !answerStatsEligible) {
      setAnswerStatsByKey({});
      setAnswerStatsLoading(false);
      setNearestAnswerChart(null);
      return;
    }

    let cancelled = false;
    const usageHistory = Array.isArray(editData.usageHistory) ? editData.usageHistory : [];
    const answerList = buildAnswerList();

    if (!isNearestAnswerStats && answerList.length === 0) {
      setAnswerStatsByKey({});
      setAnswerStatsLoading(false);
      setNearestAnswerChart(null);
      return;
    }

    const emptyStats = {};
    if (!isNearestAnswerStats) {
      answerList.forEach((item) => {
        emptyStats[item.key] = {
          percent: null,
          percentText: "",
          countText: "0/0",
          tooltip: "Chosen 0 times out of 0 responses",
          total: 0,
          chosen: 0
        };
      });
    }

    if (usageHistory.length === 0) {
      setAnswerStatsByKey(emptyStats);
      setAnswerStatsLoading(false);
      setNearestAnswerChart(isNearestAnswerStats ? {
        bins: [],
        labels: [],
        maxCount: 0,
        total: 0
      } : null);
      return;
    }

    setAnswerStatsLoading(true);

    (async () => {
      try {
        const gameIds = Array.from(
          new Set(
            usageHistory
              .map((entry) => entry?.gameId)
              .filter((gameId) => typeof gameId === "string" && gameId.trim() !== "")
          )
        );

        const answerCount = answerList.length;
        const answerCounts = new Array(answerCount).fill(0);
        const correctPositionCounts = new Array(answerCount).fill(0);
        const numericAnswers = [];
        let totalResponses = 0;
        let totalCorrect = 0;

        for (const gameId of gameIds) {
          const gameSnap = await getDoc(doc(db, "games", gameId));
          if (!gameSnap.exists()) continue;
          const gameData = gameSnap.data() || {};
          const questionsArr = Array.isArray(gameData.questions)
            ? gameData.questions
            : Object.values(gameData.questions || {});

          if (!Array.isArray(questionsArr) || questionsArr.length === 0) continue;

          const questionIndexes = [];
          questionsArr.forEach((q, idx) => {
            if (q && q._libraryQuestionId === editData.id) {
              questionIndexes.push(idx);
            }
          });

          if (questionIndexes.length === 0) continue;
          const questionIndexSet = new Set(questionIndexes);

          const answersSnap = await getDocs(collection(db, "games", gameId, "answers"));
          answersSnap.forEach((answerDoc) => {
            const ans = answerDoc.data();
            if (!ans || ans.q === undefined || ans.q === null) return;
            const qIndex = typeof ans.q === "string" ? parseInt(ans.q, 10) : ans.q;
            if (!Number.isFinite(qIndex) || !questionIndexSet.has(qIndex)) return;

            totalResponses += 1;
            if (isCorrectOnlyStats && ans.correct === true) {
              totalCorrect += 1;
            }

            if (isNearestAnswerStats) {
              const num = typeof ans.answer === "number" ? ans.answer : parseFloat(ans.answer);
              if (Number.isFinite(num)) {
                numericAnswers.push({
                  value: num,
                  team: ans.team || "Unknown"
                });
              }
            } else if (isPositionStats) {
              if (Array.isArray(ans.answer)) {
                ans.answer.forEach((idx, position) => {
                  const parsed = typeof idx === "string" ? parseInt(idx, 10) : idx;
                  if (Number.isFinite(parsed) && parsed === position && position < answerCount) {
                    correctPositionCounts[position] += 1;
                  }
                });
              }
            } else if (Array.isArray(ans.answer)) {
              ans.answer.forEach((idx) => {
                const parsed = typeof idx === "string" ? parseInt(idx, 10) : idx;
                if (Number.isFinite(parsed) && parsed >= 0 && parsed < answerCount) {
                  answerCounts[parsed] += 1;
                }
              });
            } else {
              const parsed = typeof ans.answer === "string" ? parseInt(ans.answer, 10) : ans.answer;
              if (Number.isFinite(parsed) && parsed >= 0 && parsed < answerCount) {
                answerCounts[parsed] += 1;
              }
            }
          });
        }

        const statsByKey = {};
        if (isNearestAnswerStats) {
          const correctValue = typeof editData.answer === "number"
            ? editData.answer
            : parseFloat(editData.answer);
          const hasCorrectValue = Number.isFinite(correctValue);
          const numericValues = numericAnswers.map((item) => item.value);
          const values = hasCorrectValue ? [...numericValues, correctValue] : numericValues;
          const minValue = values.length ? Math.min(...values) : 0;
          const maxValue = values.length ? Math.max(...values) : 0;
          const range = maxValue - minValue || 1;
          const totalBins = 20;
          const bins = new Array(totalBins).fill(0);
          const labels = Array.from({ length: totalBins }, (_, idx) => {
            const startPct = idx * 5;
            const endPct = startPct + 5;
            const startValue = minValue + (range * startPct) / 100;
            const endValue = minValue + (range * endPct) / 100;
            const midValue = (startValue + endValue) / 2;
            const format = (v) => (Number.isInteger(v) ? `${v}` : v.toFixed(1));
            return `${format(midValue)}`;
          });

          numericValues.forEach((value) => {
            const pct = ((value - minValue) / range) * 100;
            const idx = Math.max(0, Math.min(totalBins - 1, Math.floor(pct / 5)));
            bins[idx] += 1;
          });

          const maxCount = bins.length ? Math.max(...bins) : 0;
          setNearestAnswerChart({
            bins,
            labels,
            maxCount,
            total: numericValues.length,
            minValue,
            maxValue,
            correctValue: hasCorrectValue ? correctValue : null
          });
        } else if (isCorrectOnlyStats) {
          const total = totalResponses || 0;
          const hasData = total > 0;
          const percent = hasData ? Math.round((totalCorrect / total) * 100) : null;
          statsByKey.answer = {
            percent,
            percentText: hasData ? `${percent}%` : "",
            countText: `${totalCorrect}/${total}`,
            tooltip: `Correct ${totalCorrect} out of ${total} responses`,
            total,
            chosen: totalCorrect
          };
        } else if (isPositionStats) {
          answerList.forEach((item, idx) => {
            const correctCount = correctPositionCounts[idx] || 0;
            const total = totalResponses || 0;
            const hasData = total > 0;
            const percent = hasData ? Math.round((correctCount / total) * 100) : null;
            statsByKey[item.key] = {
              percent,
              percentText: hasData ? `${percent}%` : "",
              countText: `${correctCount}/${total}`,
              tooltip: `Correct position ${correctCount} out of ${total} responses`,
              total,
              chosen: correctCount
            };
          });
        } else {
          answerList.forEach((item, idx) => {
            const chosen = answerCounts[idx] || 0;
            const total = totalResponses || 0;
            const hasData = total > 0;
            const percent = hasData ? Math.round((chosen / total) * 100) : null;
            statsByKey[item.key] = {
              percent,
              percentText: hasData ? `${percent}%` : "",
              countText: `${chosen}/${total}`,
              tooltip: `Chosen ${chosen} times out of ${total} responses`,
              total,
              chosen
            };
          });
        }

        if (!cancelled) {
          setAnswerStatsByKey(statsByKey);
          if (!isNearestAnswerStats) {
            setNearestAnswerChart(null);
          }
          setAnswerStatsLoading(false);
        }
      } catch (err) {
        console.error("Failed to fetch answer stats:", err);
        if (!cancelled) {
          setAnswerStatsByKey(emptyStats);
          setAnswerStatsLoading(false);
          setNearestAnswerChart(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [editData?.id, editData?.usageHistory, answerStatsEligible]);




  

  return (
    <>
      <BobModal
        open={!!bobModalMessage}
        onClose={() => setBobModalMessage("")}
        message={bobModalMessage}
        confirmText="OK"
        title="Quizmaster Bob says"
      />
      {validationWarning && <BobPopup message={validationWarning} onClose={() => setValidationWarning(null)} />}
      <MediaPopup url={mediaPopupUrl} onClose={() => setMediaPopupUrl(null)} onDelete={handleDeleteMedia} />


      <AudioPopup url={audioPopupUrl} onClose={() => setAudioPopupUrl(null)} onDelete={handleDeleteAudio} />

        
      {showDeleteConfirm && <ConfirmationPopup message="Are you sure you want to delete this question? This action cannot be undone." onConfirm={handleConfirmDelete} onCancel={() => setShowDeleteConfirm(false)} />}
      

{/* --- ADD POPUP RENDER HERE --- */}
      {showUsageHistoryPopup && editData && (
        <UsageHistoryPopup
          history={editData.usageHistory || []}
          questionId={editData.id}
          onDeleteEntry={handleDeleteUsageEntry}
          onClose={() => setShowUsageHistoryPopup(false)}
        />
      )}
      {/* --- END POPUP RENDER --- */}

      {/* Top Bar with Menu Button (same chrome as other admin screens) */}
      <div
        style={{
          width: "100%",
          padding: "14px 16px",
          background: "rgba(20,15,44,0.55)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          position: "sticky",
          top: 0,
          zIndex: 999,
        }}
      >
        <button
          aria-label="Open menu"
          title="Menu"
          onClick={() => setShowMenu(true)}
          style={{
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.16)",
            color: "#ffe066",
            fontWeight: 900,
            borderRadius: 12,
            padding: "10px 14px",
            cursor: "pointer",
          }}
        >
          ☰ Menu
        </button>

        <div
          style={{
            marginLeft: 8,
            fontWeight: 900,
            color: "#f8faff",
          }}
        >
          Library Viewer
        </div>
      </div>

      {/* Sidebar instance */}
      <Menu open={showMenu} onClose={() => setShowMenu(false)} />

      <div className="library-viewer-fullscreen">
        <div className="library-header">
          <div className="library-nav">
            <button onClick={handlePrev}>&larr; Prev</button>
            <span>
              {isCreatingNew ? "New" : (filteredQuestions.length > 0 ? activeIndex + 1 : 0)} / {filteredQuestions.length}
            </span>
            <button onClick={handleNext}>Next &rarr;</button>
            <button onClick={handleAddClick} className="add-btn">New</button>
            <button onClick={() => setShowDeleteConfirm(true)} className="delete-btn" disabled={!editData || isCreatingNew}>Delete</button>
          </div>
          <div className="library-title">Library Viewer</div>
          <div className="library-actions">
             {message && <span className="save-message">{message}</span>}
             <input
                type="text"
                placeholder="Search..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {filteredQuestions.length === 0 && (searchTerm || filters.flaggedFilter !== "All" || filters.musicFilter !== "All" || filters.genreFilter || filters.categoryFilter || filters.venueFilter || filters.hashtagFilter) && <span className="no-results-message">No results found</span>}
            <button onClick={() => setSettingsOpen(true)}>Settings</button>
          </div>
        </div>

        {loading ? (
          <div className="fullscreen-center">Loading Library...</div>
        ) : !editData ? (
          <div className="fullscreen-center">
            {questions.length === 0 ? "Database is empty." : "No question selected."}
            {isCreatingNew && <span> (Error: New form not loading)</span>}
          </div>
        ) : (
          <div className="editor-main-content">
            <div className="editor-left-panel">
              <div className="question-section">
                <textarea value={editData.question || ""} onChange={(e) => handleInputChange('question', e.target.value)} className="question-textarea" placeholder="Type new question..."/>
              </div>
              <div className="answers-section-editable">
                  {category === 'TRUE/FALSE' ? (
                    <>
                      <AnswerButton 
                        key="answer" 
                        answerKey="answer" 
                        value={editData.answer} 
                        isCorrect={true} 
                        questionType={category} 
                        onToggle={handleTrueFalseToggle}
                        onInputChange={()=>{}} 
                        stats={answerStatsByKey.answer}
                        statsLoading={answerStatsLoading}
                        showStats={answerStatsEligible && !isNearestAnswerStats && !!(editData.answer && String(editData.answer).trim() !== "")}
                      />
                      <AnswerButton 
                        key="A2" 
                        answerKey="A2" 
                        value={editData.A2} 
                        isCorrect={false} 
                        questionType={category} 
                        onToggle={handleTrueFalseToggle}
                        onInputChange={()=>{}} 
                        stats={answerStatsByKey.A2}
                        statsLoading={answerStatsLoading}
                        showStats={answerStatsEligible && !isNearestAnswerStats && !!(editData.A2 && String(editData.A2).trim() !== "")}
                      />
                    </>
                  ) : category === "OUTOFORDER" ? (
                    <DragDropContext onDragEnd={handleOutOfOrderDragEnd}>
                      <Droppable droppableId="library-ooo-answers">
                        {(dropProvided) => (
                          <div
                            ref={dropProvided.innerRef}
                            {...dropProvided.droppableProps}
                            className="ooo-answer-dnd-list"
                          >
                            {ANSWER_KEYS.filter(
                              (k) =>
                                editData[k] != null && String(editData[k] ?? "").trim() !== ""
                            ).map((key, index) => (
                              <Draggable key={key} draggableId={`ooo-${key}`} index={index}>
                                {(dragProvided) => (
                                  <div
                                    className="ooo-answer-dnd-row"
                                    ref={dragProvided.innerRef}
                                    {...dragProvided.draggableProps}
                                  >
                                    <span className="ooo-answer-order-badge" aria-hidden="true">
                                      {index + 1}
                                    </span>
                                    <div
                                      className="ooo-answer-drag-handle"
                                      {...dragProvided.dragHandleProps}
                                      title="Drag to reorder (1 = first in correct order)"
                                    >
                                      ☰
                                    </div>
                                    <div className="ooo-answer-button-wrap">
                                      <AnswerButton
                                        answerKey={key}
                                        value={editData[key]}
                                        isCorrect={key === "answer"}
                                        questionType={category}
                                        onInputChange={handleInputChange}
                                        answerIndex={index}
                                        stats={answerStatsByKey[key]}
                                        statsLoading={answerStatsLoading}
                                        showStats={
                                          answerStatsEligible &&
                                          !isNearestAnswerStats &&
                                          !!(editData[key] && String(editData[key]).trim() !== "")
                                        }
                                      />
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {dropProvided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  ) : (
                    getAnswerKeysForCategory(category).map((key, index) => {
                      if (isSingleAnswer && index > 0) return null;
                      
                      const isMultiAnswerType = category === "MULTIANSWER";
                      if (isMultiAnswerType) {
                        if (editData[key] === undefined || editData[key] === null) return null;
                      }
                      
                      const isBreadcrumbsClue = category === 'BREADCRUMBS' && BREADCRUMBS_CLUE_KEYS.includes(key);
                      const clueNum = BREADCRUMBS_CLUE_KEYS.indexOf(key) + 1;
                      const displayLabel = isBreadcrumbsClue
                        ? (clueNum === 1 ? "Clue 1 (most difficult)" : clueNum === 2 ? "Clue 2 (easiest)" : `Clue ${clueNum}`)
                        : undefined;
                      const useClueIcon = isBreadcrumbsClue;
                      
                      return (
                        <AnswerButton
                          key={key}
                          answerKey={key}
                          value={editData[key]}
                          isCorrect={key === 'answer'}
                          questionType={category}
                          onInputChange={handleInputChange}
                          answerIndex={index}
                          stats={answerStatsByKey[key]}
                          statsLoading={answerStatsLoading}
                          showStats={answerStatsEligible && !isNearestAnswerStats && !!(editData[key] && String(editData[key]).trim() !== "")}
                          displayLabel={displayLabel}
                          useClueIcon={useClueIcon}
                        />
                      );
                    })
                  )}
              </div>

              {/* Visible Explanation Field */}
              <div style={{ marginTop: '20px' }}>
                <input
                  type="text"
                  value={editData.visibleExplanation || ""}
                  onChange={(e) => handleInputChange('visibleExplanation', e.target.value)}
                  className="visible-explanation-input"
                  placeholder="Visible Explanation (optional)..."
                  maxLength={130}
                />
              </div>

          <div style={{ marginTop: '15px', marginBottom: '10px', textAlign: 'center' }}>
            <button
              onClick={() => setShowUsageHistoryPopup(true)}
              disabled={!editData || isCreatingNew}
              style={{
                padding: '8px 16px',
                fontSize: '0.9rem',
                // Add styles similar to other buttons if desired
              }}
            >
              View Question History ({editData?.usageHistory?.length || 0})
            </button>
          </div>







           {/* --- WRAPPER FOR MEDIA BOXES --- */}
                <div style={{ display: 'flex', gap: '20px' }}>
                
                  {/* --- EXISTING MEDIA PREVIEW BOX --- Only show for non-WHEEL categories */}
                  {category !== 'WHEEL' && (
                    <>
                      <div className="media-section-preview" onClick={handleMediaClick}>
                        <div className="media-preview-box">
                          {isUploading ? (<div className="media-upload-prompt-small">Uploading...</div>
                          ) : (editData.Medialink) ? (
                            String(editData.Medialink).match(/\.(jpeg|jpg|gif|png|svg|webp)/i) ?
                                <img src={editData.Medialink} alt="Question media" /> :
                                <video src={editData.Medialink} muted loop playsInline />
                          ) : (
                            <div className="media-upload-prompt-small">
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16"><path d="M4.502 9.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z"/><path d="M14.002 13.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zM11.5 6.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zM6 11.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zM1.5 6.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z"/><path d="M.5 1a.5.5 0 0 0 0 1h1.11l.401 1.604 1.385-2.422a.5.5 0 0 1 .866.499l-1.38 2.415-2.09 3.655A.5.5 0 0 0 1.5 8h.488l.89-1.557a.5.5 0 0 1 .866.5l-1.11 1.932.12.21a.5.5 0 0 0 .434.252h.932l.89-1.556a.5.5 0 0 1 .866.5l-1.38 2.415 1.255 2.197a.5.5 0 0 0 .434.252h.985l.522-1.044a.5.5 0 0 1 .894.448l-.522 1.044.12.21a.5.5 0 0 0 .434.252h1.59a.5.5 0 0 0 0-1h-1.28l-.523-1.045a.5.5 0 0 1 .894-.447l.523 1.045h.985a.5.5 0 0 0 .434-.252l1.38-2.415-2.09-3.655a.5.5 0 0 0-.866-.5l-1.38 2.415-1.255-2.197a.5.5 0 0 0-.434-.252h-.985l-.522 1.044a.5.5 0 0 1-.894-.448l.522-1.044h-.932a.5.5 0 0 0-.434.252L6.1 7.443l-1.11-1.932a.5.5 0 0 1 .866-.5l.89 1.557h.488a.5.5 0 0 0 .434-.252l1.38-2.415-2.42-4.236A.5.5 0 0 0 6.22 2H5.12a.5.5 0 0 0 0 1h.985l.25 1H.5a.5.5 0 0 0-.5-.5z"/></svg>
                              <span>Upload Media</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* --- HIDDEN FILE INPUT FOR MEDIA --- */}
                      <input type="file" ref={fileInputRef} onChange={handleFileUpload} style={{ display: 'none' }} accept="image/*,video/*" />
                    </>
                  )}

                  {/* --- NEW AUDIO PREVIEW BOX --- Show for all categories that support audio (MULTICHOICE, WHEEL, etc.) */}
                  <div className="media-section-preview" onClick={handleAudioClick} style={{width: '200px'}}>
                    <div className="media-preview-box">
                      {isUploading ? (<div className="media-upload-prompt-small">Uploading...</div>
                      ) : (editData.Audiolink) ? (
                        <div style={{ padding: '10px', width: '100%', boxSizing: 'border-box', textAlign: 'center' }}>
                          <span style={{ fontSize: '0.9rem', marginBottom: '10px', display: 'block', color: '#888' }}>Audio File</span>
                          <audio 
                            ref={audioPlayerRef}
                            src={editData.Audiolink} 
                            controls 
                            style={{ width: '100%' }}
                          >
                            Your browser does not support the audio element.
                          </audio>
                        </div>
                      ) : (
                        <div className="media-upload-prompt-small">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M9 13c0 1.105-1.12 2-2.5 2S4 14.105 4 13s1.12-2 2.5-2 2.5.895 2.5 2z"/>
                            <path fillRule="evenodd" d="M9 3v10H8V3h1z"/>
                            <path d="M8 2.5a.5.5 0 0 1 .5.5v10a.5.5 0 0 1-1 0v-10a.5.5 0 0 1 .5-.5z"/>
                          </svg>
                          <span>Upload MP3</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* --- HIDDEN FILE INPUT FOR AUDIO --- */}
                  <input type="file" ref={audioFileInputRef} onChange={handleAudioFileUpload} style={{ display: 'none' }} accept="audio/*" />

                  {/* --- MP3 FROM CSV SECTION --- */}
                  <div style={{ marginTop: '20px', width: '100%', maxWidth: '600px' }}>
                    <div style={{ fontWeight: 900, fontSize: '0.9rem', marginBottom: 10, color: '#fff' }}>
                      Upload MP3 from Exportify CSV
                    </div>
                    <button
                      onClick={() => setShowCsvUploader(prev => !prev)}
                      style={{
                        background: '#1f7a5b',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        padding: '8px 12px',
                        fontWeight: 800,
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        width: '100%',
                        marginBottom: 10
                      }}
                    >
                      {showCsvUploader ? "Hide CSV Tracks" : "Upload MP3 from Exportify CSV"}
                    </button>

                    {showCsvUploader && (
                      <div style={{ background: 'rgba(0,0,0,0.2)', padding: 12, borderRadius: 10 }}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                          <button
                            onClick={() => csvFileInputRef.current?.click()}
                            style={{
                              background: '#0d6efd',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 8,
                              padding: '6px 10px',
                              fontWeight: 800,
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }}
                          >
                            Choose CSV
                          </button>
                          <button
                            onClick={clearCsvTracks}
                            style={{
                              background: '#6c757d',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 8,
                              padding: '6px 10px',
                              fontWeight: 800,
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }}
                          >
                            Clear CSV
                          </button>
                          <button
                            onClick={() => {
                              setBobModalMessage(
                                "1) Create Spotify Playlist\n2) Go to https://exportify.app/ and export playlist to your local machine\n3) Select the downloaded file"
                              );
                            }}
                            style={{
                              background: '#198754',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 8,
                              padding: '6px 10px',
                              fontWeight: 800,
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }}
                          >
                            Instructions
                          </button>
                        </div>
                        <input
                          type="file"
                          ref={csvFileInputRef}
                          onChange={handleCsvFileUpload}
                          accept=".csv"
                          style={{ display: 'none' }}
                        />

                        {csvFileName && (
                          <div style={{ fontSize: '0.8rem', color: '#ccc', marginBottom: 8 }}>
                            Loaded: {csvFileName} ({visibleCsvTracks.length} available)
                          </div>
                        )}
                        {csvError && (
                          <div style={{ fontSize: '0.8rem', color: '#ff6b6b', marginBottom: 8 }}>
                            {csvError}
                          </div>
                        )}

                        {csvTracks.length === 0 && (
                          <div style={{ fontSize: '0.8rem', color: '#aaa', marginBottom: 8 }}>
                            Upload a CSV with columns: Track Name, Artist Name(s), Track Preview URL.
                          </div>
                        )}

                        {csvTracks.length > 0 && visibleCsvTracks.length === 0 && (
                          <div style={{ fontSize: '0.8rem', color: '#aaa', marginBottom: 8 }}>
                            All tracks from this CSV were used this session.
                          </div>
                        )}

                        {sortedCsvTracks.length > 0 && (
                          <div>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 8,
                                marginBottom: 6,
                                position: "sticky",
                                top: 0,
                                zIndex: 1,
                                background: "rgba(0,0,0,0.65)",
                                padding: "6px 0"
                              }}
                            >
                              <button
                                onClick={() =>
                                  setCsvSortConfig((prev) => ({
                                    key: "trackName",
                                    direction:
                                      prev.key === "trackName" && prev.direction === "asc"
                                        ? "desc"
                                        : "asc"
                                  }))
                                }
                                style={{
                                  background: "rgba(255,255,255,0.08)",
                                  color: "#fff",
                                  border: "1px solid rgba(255,255,255,0.15)",
                                  borderRadius: 8,
                                  padding: "6px 10px",
                                  textAlign: "left",
                                  cursor: "pointer",
                                  fontSize: "0.75rem",
                                  fontWeight: 800
                                }}
                              >
                                Track Name {csvSortConfig.key === "trackName" ? (csvSortConfig.direction === "asc" ? "▲" : "▼") : ""}
                              </button>
                              <button
                                onClick={() =>
                                  setCsvSortConfig((prev) => ({
                                    key: "artistNames",
                                    direction:
                                      prev.key === "artistNames" && prev.direction === "asc"
                                        ? "desc"
                                        : "asc"
                                  }))
                                }
                                style={{
                                  background: "rgba(255,255,255,0.08)",
                                  color: "#fff",
                                  border: "1px solid rgba(255,255,255,0.15)",
                                  borderRadius: 8,
                                  padding: "6px 10px",
                                  textAlign: "left",
                                  cursor: "pointer",
                                  fontSize: "0.75rem",
                                  fontWeight: 800
                                }}
                              >
                                Artist {csvSortConfig.key === "artistNames" ? (csvSortConfig.direction === "asc" ? "▲" : "▼") : ""}
                              </button>
                            </div>
                            <div style={{ maxHeight: 220, overflowY: 'auto', display: 'grid', gap: 6 }}>
                            {sortedCsvTracks.map((track, index) => {
                              const isSuggested =
                                suggestedCsvPreviewUrlSet &&
                                suggestedCsvPreviewUrlSet.has(normalizeUrlKey(track.previewUrl));
                              const isChosenForUpload =
                                chosenCsvTrackUrl &&
                                normalizeUrlKey(track.previewUrl) === normalizeUrlKey(chosenCsvTrackUrl);
                              const useGreen = isChosenForUpload;
                              const useYellow = isSuggested && !useGreen;
                              return (
                                <button
                                  key={`${normalizeUrlKey(track.previewUrl)}-${index}`}
                                  ref={(node) => {
                                    const key = normalizeUrlKey(track.previewUrl);
                                    if (!key) return;
                                    if (node) csvTrackRefs.current[key] = node;
                                  }}
                                  onClick={() => handleCsvTrackUpload(track)}
                                  style={{
                                    background: useGreen
                                      ? "rgba(76, 175, 80, 0.25)"
                                      : useYellow
                                        ? "rgba(255, 193, 7, 0.18)"
                                        : "rgba(255,255,255,0.08)",
                                    color: "#fff",
                                    border: useGreen
                                      ? "1px solid rgba(76, 175, 80, 0.9)"
                                      : useYellow
                                        ? "1px solid rgba(255, 193, 7, 0.8)"
                                        : "1px solid rgba(255,255,255,0.15)",
                                    borderRadius: 8,
                                    padding: "8px 10px",
                                    textAlign: "left",
                                    cursor: "pointer",
                                    fontSize: "0.82rem",
                                    boxShadow: useGreen
                                      ? "0 0 0 1px rgba(76, 175, 80, 0.4)"
                                      : useYellow
                                        ? "0 0 0 1px rgba(255, 193, 7, 0.35)"
                                        : "none"
                                  }}
                                >
                                  {useGreen && (
                                    <div style={{ fontSize: "0.7rem", color: "#81c784", marginBottom: 2 }}>
                                      Uploading / chosen
                                    </div>
                                  )}
                                  {useYellow && !useGreen && (
                                    <div style={{ fontSize: "0.7rem", color: "#ffd66e", marginBottom: 2 }}>
                                      Suggested match
                                    </div>
                                  )}
                                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {track.trackName || "Unknown Track"}
                                    </div>
                                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", opacity: 0.85 }}>
                                      {track.artistNames || ""}
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                </div> {/* --- END WRAPPER --- */}
                {answerStatsEligible && (
                  <div className="answer-stats-panel">
                    <div className="answer-stats-header">Answer Stats</div>
                    {answerStatsLoading && (
                      <div className="answer-stats-subtext">Loading stats...</div>
                    )}
                    {!answerStatsLoading && isNearestAnswerStats && nearestAnswerChart && (
                      <div className="nearest-answer-chart">
                        <div className="nearest-answer-header">
                          <div className="nearest-answer-title">Nearest Answer Distribution</div>
                          {Number.isFinite(nearestAnswerChart.correctValue) && (
                            <div className="nearest-answer-correct">
                              {`Correct Answer (${nearestAnswerChart.correctValue})`}
                            </div>
                          )}
                        </div>
                        <div className="nearest-answer-chart-bars">
                          <div className="nearest-answer-y-axis">
                            {Array.from({ length: 5 }).map((_, idx) => {
                              const tickCount = nearestAnswerChart.maxCount
                                ? Math.round((nearestAnswerChart.maxCount * (4 - idx)) / 4)
                                : 0;
                              return (
                                <div key={`near-y-${idx}`} className="nearest-answer-y-tick">
                                  <span>{tickCount}</span>
                                  <div className="nearest-answer-y-line" />
                                </div>
                              );
                            })}
                          </div>
                          <div className="nearest-answer-bars">
                            {nearestAnswerChart.bins.map((count, idx) => {
                              const heightPct = nearestAnswerChart.maxCount > 0
                                ? Math.round((count / nearestAnswerChart.maxCount) * 100)
                                : 0;
                              return (
                                <div key={`near-bin-${idx}`} className="nearest-answer-bin">
                                  <div className="nearest-answer-count">{count}</div>
                                  <div className="nearest-answer-bar" style={{ height: `${heightPct}%` }} />
                                  <div className="nearest-answer-label">{nearestAnswerChart.labels[idx]}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div className="nearest-answer-summary">
                          {`Responses: ${nearestAnswerChart.total}`}
                        </div>
                      </div>
                    )}
                  </div>
                )}
            </div>

            <div className="editor-right-panel">
                <div className="details-grid">
                    <div className="detail-item">
                      <label>Category</label>
                      <select value={category} onChange={(e) => handleCategoryChange(e.target.value)} style={{backgroundColor: style.background, color: style.color}}>
                        <option value="">Select Type</option>
                        {questionTypeOptions.map(t => <option key={t} value={t} style={{backgroundColor: categoryStyles[t]?.background, color: categoryStyles[t]?.color}}>{t}</option>)}
                      </select>
                    </div>
                    <div className="detail-item">
                      <label>Genre</label>
                      <select value={editData.genre || ""} onChange={(e) => handleInputChange('genre', e.target.value)}>
                        <option value="">Select Genre</option>
                        {genreOptions.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </div>
                    <SliderInput label="Difficulty" value={editData.difficulty} onChange={val => handleInputChange('difficulty', val)} />
                    <SliderInput label="Interest Rating" value={editData.interestRating} onChange={val => handleInputChange('interestRating', val)} />
                    <SliderInput label="Min Age" value={editData.minAge} onChange={val => handleInputChange('minAge', val)} />
                    <SliderInput label="Max Age" value={editData.maxAge} onChange={val => handleInputChange('maxAge', val)} />
                    <div className="detail-item">
                      <div className="label-with-button">
                        <label>Flag</label>
                        <button className="clear-flag-btn" onClick={() => handleInputChange('accuracyFlag', '')}>Clear</button>
                      </div>
                      <input type="text" value={editData.accuracyFlag || ""} onChange={(e) => handleInputChange('accuracyFlag', e.target.value)} />
                    </div>
                    <div className="detail-item"><label>Time Sensitivity</label><input type="text" value={editData.timeSensitivityYear || ""} onChange={(e) => handleInputChange('timeSensitivityYear', e.target.value)} /></div>
                    <div className="detail-item full-width"><label>Interest Justification</label><textarea value={editData.interestRatingJustification || ""} onChange={(e) => handleInputChange('interestRatingJustification', e.target.value)} /></div>
                    <div className="detail-item full-width"><label>Demographic Appeal</label><input type="text" value={Array.isArray(editData.demographicAppeal) ? editData.demographicAppeal.join(', ') : ''} onChange={(e) => handleArrayChange('demographicAppeal', e.target.value)} placeholder="comma, separated, values"/></div>
                    <div className="detail-item full-width"><label>Tags</label><input type="text" value={tagsInput} onChange={(e) => { setTagsInput(e.target.value); handleArrayChange('tags', e.target.value); }} onBlur={() => { if (editData?.tags) { setTagsInput(Array.isArray(editData.tags) ? editData.tags.join(", ") : String(editData.tags || "")); } }} placeholder="comma, separated, values"/></div>
                    <div className="detail-item full-width"><label>Geographic Relevance</label><input type="text" value={Array.isArray(editData.geographicRelevance) ? editData.geographicRelevance.join(', ') : ''} onChange={(e) => handleArrayChange('geographicRelevance', e.target.value)} placeholder="comma, separated, values"/></div>
                    <div className="detail-item full-width"><label>Media Suggestion</label><input type="text" value={editData.mediaSuggestion || ""} onChange={(e) => handleInputChange('mediaSuggestion', e.target.value)} /></div>
                    <div className="detail-item full-width"><label>Notes</label><textarea value={editData.notes || ""} onChange={(e) => handleInputChange('notes', e.target.value)} /></div>
                    <div className="detail-item readonly"><label>Date Added</label><span>{editData.dateAdded ? new Date(editData.dateAdded).toLocaleDateString() : "-"}</span></div>
                    <div className="detail-item readonly full-width"><label>Last Reviewed By</label><span>{editData.lastReviewedBy || "-"}</span></div>
                    <div className="detail-item readonly full-width"><label>Last Reviewed Date</label><span>{editData.lastReviewedDate ? new Date(editData.lastReviewedDate).toLocaleString() : "-"}</span></div>
                </div>
            </div>
          </div>
        )}
       
       <SettingsModal
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          filters={filters}
          setFilters={setFilters}
          onReset={handleResetSettings}
          onOpenTagStats={handleOpenTagStats}
          onSortChange={(field, dir) => { setSortField(field); setSortDirection(dir); }}
          currentSortField={sortField}
          currentSortDirection={sortDirection}
          dbStats={dbStats}
          genreOptions={genreOptions} // <-- Ensure this prop is passed
          questionTypeOptions={questionTypeOptions} // <-- Ensure this prop is passed
          venueOptions={venueOptions} // <-- Pass venue options
        />
        {tagStatsOpen && tagStatsSnapshot ? (
          <TagStatsModal questions={tagStatsSnapshot} onClose={handleCloseTagStats} />
        ) : null}
       
       
        </div>
    </>
  );
}

const styles = `
.fullscreen-center { display: flex; justify-content: center; align-items: center; width: 100vw; height: 100vh; font-size: 1.5rem; background: #282c34; color: white; }
.library-viewer-fullscreen { display: flex; flex-direction: column; width: 100vw; height: 100vh; background: #282c34; color: white; font-family: sans-serif; }
.library-header { display: flex; justify-content: space-between; align-items: center; padding: 10px 20px; background: #20232a; border-bottom: 1px solid #444; flex-shrink: 0; }
.library-nav, .library-actions { display: flex; align-items: center; gap: 15px; }
.library-title { font-size: 1.5rem; font-weight: bold; }
.library-header button { background: #61dafb; border: none; padding: 8px 15px; border-radius: 5px; cursor: pointer; font-weight: bold; }
.library-header button:disabled { background: #555; cursor: not-allowed; }
.save-message { color: #4caf50; font-style: italic; margin-right: 15px;}
.search-input { background: #333; color: white; border: 1px solid #555; border-radius: 5px; padding: 8px 12px; font-size: 0.9rem; width: 200px; }
.no-results-message { color: #ffc107; font-style: italic; font-size: 0.9rem; }
.add-btn { background-color: #28a745; color: white; }
.add-btn:hover { background-color: #218838; }
.delete-btn { background-color: #dc3545; color: white; }
.delete-btn:hover { background-color: #c82333; }
.editor-main-content { display: flex; flex-grow: 1; padding: 20px; gap: 20px; overflow: hidden; }
.editor-left-panel, .editor-right-panel { display: flex; flex-direction: column; gap: 20px; overflow-y: auto; padding: 5px; min-width: 0; }
.editor-left-panel { flex: 2; }
.editor-right-panel { flex: 1; background: #20232a; padding: 20px; border-radius: 8px; }
.question-section { position: relative; }
.question-textarea { width: 100%; height: 150px; background: #333; color: white; border: 1px solid #555; border-radius: 8px; padding: 15px; font-size: 1.2rem; resize: vertical; box-sizing: border-box; }
.answers-section-editable { display: flex; flex-direction: column; gap: 10px; margin-top: 20px; }
.ooo-answer-dnd-list { display: flex; flex-direction: column; gap: 10px; width: 100%; }
.ooo-answer-dnd-row { display: flex; flex-direction: row; align-items: stretch; gap: 10px; width: 100%; max-width: 100%; min-width: 0; }
.ooo-answer-order-badge { flex: 0 0 auto; min-width: 2rem; display: flex; align-items: center; justify-content: center; font-family: 'Arial Black', Impact, sans-serif; font-size: 1.25rem; font-weight: 900; color: #f8faff; text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000; user-select: none; }
.ooo-answer-drag-handle { flex: 0 0 auto; display: flex; align-items: center; justify-content: center; min-width: 2rem; cursor: grab; color: #9bb3d3; font-weight: 900; font-size: 1.2rem; user-select: none; line-height: 1; }
.ooo-answer-drag-handle:active { cursor: grabbing; }
.ooo-answer-button-wrap { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.ooo-answer-button-wrap .answer-button-editable { width: 100%; }
.answer-button-editable { display: flex; align-items: center; gap: 8px; border-radius: 2em; padding: 5px 5px 5px 15px; font-family: 'Arial Black', Impact, sans-serif; font-weight: 900; color: white; text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000; max-width: 100%; box-sizing: border-box; }
.answer-icon { margin-right: 10px; font-size: 1.2rem; user-select: none; }
.answer-button-editable.correct { background: linear-gradient(135deg, rgba(153,255,204,0.75), rgba(0,102,68,0.95)); }
.answer-button-editable.incorrect { background: linear-gradient(135deg, rgba(255,102,102,0.75), rgba(153,0,0,0.90)); }
.answer-button-editable.clue { background: linear-gradient(135deg, rgba(100,120,160,0.6), rgba(60,80,120,0.85)); }
.answer-button-input { flex-grow: 1; background: transparent; border: none; color: white; font-size: 1.1rem; font-weight: bold; padding: 8px; outline: none; text-shadow: inherit; min-width: 0; }
.answer-stats-panel { margin-top: 14px; padding-top: 10px; border-top: 1px solid rgba(255,255,255,0.12); }
.answer-stats-header { font-weight: 900; font-size: 0.95rem; letter-spacing: 0.3px; color: #f8faff; margin-bottom: 6px; }
.answer-stats-subtext { font-size: 0.8rem; color: #9bb3d3; }
.answer-stat-badge { min-width: 64px; height: 56px; border-radius: 50%; border: 2px solid rgba(255,255,255,0.9); background: rgba(0,0,0,0.45); display: flex; flex-direction: column; align-items: center; justify-content: center; margin-left: 6px; box-shadow: 0 2px 8px rgba(0,0,0,0.35); text-align: center; }
.answer-stat-badge.loading { opacity: 0.85; }
.answer-stat-percent { font-size: 0.95rem; font-weight: 900; line-height: 1; }
.answer-stat-count { font-size: 0.7rem; font-weight: 700; line-height: 1.1; }
.answer-stat-loading { font-size: 0.9rem; font-weight: 800; }
.nearest-answer-chart { margin-top: 10px; }
.nearest-answer-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.nearest-answer-title { font-weight: 900; font-size: 0.95rem; color: #f8faff; }
.nearest-answer-correct { font-weight: 800; font-size: 0.85rem; color: #7CFF76; }
.nearest-answer-chart-bars { display: flex; align-items: stretch; gap: 8px; height: 200px; padding: 8px; background: rgba(0,0,0,0.25); border-radius: 8px; border: 1px solid rgba(255,255,255,0.08); }
.nearest-answer-y-axis { display: flex; flex-direction: column; justify-content: space-between; width: 40px; color: #b7c7e5; font-size: 0.65rem; }
.nearest-answer-y-tick { position: relative; height: 1px; display: flex; align-items: center; gap: 6px; }
.nearest-answer-y-line { flex: 1; height: 1px; background: rgba(255,255,255,0.12); }
.nearest-answer-bars { display: flex; align-items: flex-end; gap: 4px; flex: 1; height: 100%; }
.nearest-answer-bin { display: flex; flex-direction: column; align-items: center; justify-content: flex-end; flex: 1; min-width: 0; height: 100%; }
.nearest-answer-count { font-size: 0.7rem; color: #f1f6ff; margin-bottom: 2px; font-weight: 700; }
.nearest-answer-bar { width: 100%; background: linear-gradient(180deg, #6fe3ff, #2a76ff); border-radius: 6px 6px 2px 2px; min-height: 2px; }
.nearest-answer-label { margin-top: 6px; font-size: 0.55rem; color: #b7c7e5; text-align: center; }
.nearest-answer-summary { margin-top: 6px; font-size: 0.8rem; color: #9bb3d3; }
.visible-explanation-input { width: 100%; height: 40px; background: #ffeb3b; color: #333; border: 1px solid #fbc02d; border-radius: 8px; padding: 10px 15px; font-size: 1rem; box-sizing: border-box; font-family: sans-serif; }
.visible-explanation-input::placeholder { color: #666; }
.media-section-preview { height: 150px; width: 250px; margin-top: 20px; display: flex; align-items: center; justify-content: center; border: 2px dashed #555; border-radius: 8px; cursor: pointer; overflow: hidden; background: #20232a; flex-shrink: 0;}
.media-preview-box { width: 100%; height: 100%; display: flex; justify-content: center; align-items: center; position: relative;}
.media-preview-box img, .media-preview-box video { max-width: 100%; max-height: 100%; object-fit: contain; }
.media-upload-prompt-small { text-align: center; color: #888; font-size: 0.8rem; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.media-upload-prompt-small svg { margin-bottom: 5px; }
.details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
.detail-item { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
.detail-item.full-width { grid-column: 1 / -1; }
.detail-item label { font-size: 0.8rem; color: #aaa; text-transform: uppercase; font-weight: bold; }
.label-with-button { display: flex; justify-content: space-between; align-items: center; }
.clear-flag-btn { padding: 2px 8px; font-size: 0.7rem; background: #6c757d; border: none; color: white; border-radius: 4px; cursor: pointer; }
.detail-item input, .detail-item select, .detail-item textarea { width: 100%; background: #333; color: white; border: 1px solid #555; border-radius: 5px; padding: 8px; box-sizing: border-box; font-family: sans-serif; font-size: 1rem; }
.detail-item select option { color: white; background: #333; }
.detail-item textarea { min-height: 80px; resize: vertical; }
.detail-item.readonly span { background: #333; padding: 8px; border-radius: 5px; min-height: 38px; display: flex; align-items: center; color: #ccc; box-sizing: border-box; word-break: break-word; }
.slider { -webkit-appearance: none; appearance: none; width: 100%; height: 8px; background: #555; outline: none; border-radius: 4px; opacity: 0.7; transition: opacity .2s; }
.slider:hover { opacity: 1; }
.slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 18px; height: 18px; background: #61dafb; cursor: pointer; border-radius: 50%; }
.slider::-moz-range-thumb { width: 18px; height: 18px; background: #61dafb; cursor: pointer; border-radius: 50%; }
.media-popup-overlay { position: fixed; inset: 0; z-index: 2000; background-color: rgba(0, 0, 0, 0.85); display: flex; align-items: center; justify-content: center; backdrop-filter: blur(5px); }
.media-popup-overlay img, .media-popup-overlay video { max-width: 90vw; max-height: 90vh; border-radius: 8px; }
.settings-modal-overlay { position: fixed; inset: 0; background-color: rgba(0, 0, 0, 0.7); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.settings-modal-content { background: #282c34; padding: 24px; border-radius: 10px; color: white; width: clamp(520px, 78vw, 1200px); border: 1px solid #444; height: 75vh; overflow: auto; }
.settings-modal-content.wide { width: clamp(620px, 86vw, 1320px); }
.settings-modal-content h2 { margin-top: 0; font-size: 1.7rem; }
.settings-fieldset { border: 1px solid #3a3f4a; border-radius: 8px; padding: 12px 14px; margin-bottom: 14px; }
.settings-fieldset legend { padding: 0 8px; font-weight: 800; font-size: 1.05rem; color: #eaf2ff; }
.settings-row { display: grid; gap: 14px; }
.settings-row.four-cols { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.settings-row.two-cols { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.setting-item { margin-top: 8px; }
.setting-item.checkbox { display: flex; align-items: center; }
.setting-item label { display: block; margin-bottom: 8px; font-size: 1.1rem; line-height: 1.2; letter-spacing: 0.2px; color: #e1e8f5; }
.setting-item select, .setting-item input[type=checkbox], .setting-item input[type=text] { width: 100%; padding: 11px 12px; background: #333; color: white; border: 1px solid #555; border-radius: 6px; font-size: 1.02rem; }
.setting-item.full-width { grid-column: 1 / -1; }
.setting-input-spaced { margin-top: 6px; }
.setting-item input[type=checkbox] { width: auto; margin-right: 10px; height: 18px; width: 18px; }
.settings-header-actions { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
.settings-modal-content .settings-header-actions button { margin-top: 0; }
.settings-close-button { background: #495057 !important; color: #fff !important; border: none; padding: 10px 18px !important; border-radius: 6px !important; cursor: pointer; font-weight: 700; }
.settings-close-button:hover { background: #5c636a !important; }
.settings-modal-content .settings-reset-button { margin-top: 0; background: #61dafb; color: #212529; border: none; padding: 10px 18px; border-radius: 6px; cursor: pointer; font-weight: 700; }
.settings-hashtag-actions-row { display: flex; gap: 12px; align-items: flex-end; flex-wrap: wrap; margin-top: 8px; }
.settings-hashtag-actions-row > .setting-item { flex: 1 1 240px; margin-top: 0; }
.settings-hashtag-stats-btn { flex: 0 0 auto; margin-top: 0 !important; margin-bottom: 2px; background: #495057 !important; color: #fff !important; border: none; padding: 11px 16px !important; border-radius: 6px !important; font-weight: 700; cursor: pointer; white-space: nowrap; }
.settings-hashtag-stats-btn:hover { background: #5c636a !important; }
.tag-stats-modal-overlay { z-index: 1001; }
.tag-stats-sub { font-size: 0.88rem; color: #9bb3d3; margin: 0 0 12px; line-height: 1.45; }
.tag-stats-empty { color: #ffc107; margin-top: 12px; }
.tag-stats-loading { color: #9bb3d3; margin-top: 12px; font-style: italic; }
.tag-stats-filter-row { display: flex; align-items: center; gap: 12px; margin: 10px 0 8px; flex-wrap: wrap; }
.tag-stats-filter-row label { font-size: 0.9rem; color: #e1e8f5; font-weight: 700; }
.tag-stats-filter-input { flex: 1 1 200px; min-width: 160px; max-width: 420px; padding: 10px 12px; border-radius: 6px; border: 1px solid #555; background: #333; color: #fff; font-size: 1rem; }
.tag-stats-pager { display: flex; align-items: center; justify-content: center; gap: 16px; margin: 12px 0 8px; flex-wrap: wrap; }
.tag-stats-pager-meta { font-size: 0.9rem; color: #9bb3d3; }
.tag-stats-pager-btn { margin-top: 0 !important; padding: 8px 16px !important; border-radius: 6px !important; background: #495057 !important; color: #fff !important; border: none !important; font-weight: 700; cursor: pointer; }
.tag-stats-pager-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.tag-stats-pager-btn:hover:not(:disabled) { background: #5c636a !important; }
.tag-stats-table { width: 100%; border-collapse: collapse; font-size: 0.95rem; margin-top: 4px; }
.tag-stats-table th, .tag-stats-table td { padding: 10px 8px; text-align: left; border-bottom: 1px solid #444; vertical-align: middle; }
.tag-stats-table th { color: #9bb3d3; font-weight: 800; font-size: 0.82rem; text-transform: uppercase; letter-spacing: 0.04em; }
.tag-stats-th-sortable { padding: 0 !important; vertical-align: bottom; }
.settings-modal-content .tag-stats-sort-btn { margin-top: 0 !important; width: 100%; display: block; text-align: left; background: transparent !important; border: none; color: #9bb3d3 !important; font: inherit; font-weight: 800; font-size: 0.82rem; text-transform: uppercase; letter-spacing: 0.04em; padding: 10px 8px; cursor: pointer; border-radius: 4px; }
.tag-stats-sort-btn:hover { color: #61dafb !important; }
.tag-stats-row-main { cursor: pointer; }
.tag-stats-row-main:hover { background: rgba(97,218,251,0.08); }
.tag-stats-row-active { background: rgba(97,218,251,0.12); }
.tag-stats-detail-panel { margin-top: 14px; padding: 12px 14px; border: 1px solid #3a3f4a; border-radius: 8px; background: #1e2229; max-height: 38vh; overflow: auto; }
.tag-stats-detail-heading { font-weight: 800; font-size: 1rem; color: #eaf2ff; margin-bottom: 10px; }
.tag-stats-question-list { margin: 0; padding-left: 1.25rem; max-height: 32vh; overflow-y: auto; }
.tag-stats-question-list li { margin: 8px 0; line-height: 1.4; color: #e8eef8; }
.tag-stats-load-more { margin: 10px 0 4px; padding: 8px 14px; border-radius: 6px; border: 1px solid #555; background: #3d4450; color: #e8eef8; font-weight: 700; cursor: pointer; }
.tag-stats-load-more:hover { background: #4a5363; }
.settings-modal-content .tag-stats-load-more { margin-top: 10px !important; background: #3d4450 !important; color: #e8eef8 !important; }
.settings-modal-content button { margin-top: 20px; background: #61dafb; border: none; padding: 10px 20px; border-radius: 5px; cursor: pointer; font-weight: bold; }
.bob-popup-overlay { position: fixed; inset: 0; z-index: 3000; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; backdrop-filter: blur(4px); }
.bob-popup-content { background: linear-gradient(135deg, #444, #222); color: white; border-radius: 15px; padding: 25px; border: 2px solid #ffc107; text-align: center; max-width: 400px; box-shadow: 0 5px 25px rgba(0,0,0,0.5); }
.bob-popup-header { font-size: 1.5rem; font-weight: bold; color: #ffc107; margin-bottom: 15px; }
.bob-popup-content p { margin: 0 0 20px; font-size: 1.1rem; line-height: 1.5; }
.bob-popup-content button { background: #ffc107; color: #212529; border: none; border-radius: 8px; padding: 10px 25px; font-size: 1rem; font-weight: bold; cursor: pointer; }
.stats-section { margin-top: 18px; padding-top: 15px; border-top: 1px solid #444; }
.stats-section h3 { font-size: 1.15rem; }
.stats-section h4 { font-size: 1rem; }
.stats-section h3, .stats-section h4 { margin-top: 0; margin-bottom: 10px; }
.stats-list { list-style: none; padding: 0; margin: 0; }
.stats-list li { display: flex; justify-content: space-between; padding: 4px 0; }
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);


